import React, { useRef, useState, useEffect } from 'react';
import { Box, Stack, Typography, Button, TextField, Chip, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, LinearProgress, CircularProgress, Switch, FormControlLabel } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import ProfileStrengthTaskService from '../../services/ProfileStrengthTaskService';
import DashboardLayout from '../Layouts/DashboardLayout';
import { getYourData, saveProfileStrengthSnapshot } from '../../utils/yourDataStore';
import logger from '../../utils/logger';
import ApiService from '../../services/api';
import { getKeywordTrackingLimit } from '../../services/SubscriptionService';

import GMBService from '../../services/GMBService';
import CompetitorDiscoveryService from '../../services/CompetitorDiscoveryService';
import VelocityService from '../../services/VelocityService';
import GMBFeedService from '../../services/GMBFeedService';
import PhoneValidationService from '../../services/PhoneValidationService';
import EditIcon from '@mui/icons-material/Edit';
import AnalysisLoadingPopup from './components/AnalysisLoadingPopup';
import SemiCircularGauge from './components/SemiCircularGauge';
import './ProfileStrengthResponsiveFixes.css';

const MainContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '40px',
  alignItems: 'flex-start',
  maxWidth: '1200px',
  width: '100%',
  [theme.breakpoints.down('lg')]: {
    gap: '32px'
  },
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    gap: '24px'
  },
  [theme.breakpoints.down('sm')]: {
    gap: '16px'
  }
}));

const ContentSection = styled(Stack)(({ theme }) => ({
  flex: 1,
  gap: '40px',
  width: '100%',
  [theme.breakpoints.down('md')]: {
    gap: '32px'
  },
  [theme.breakpoints.down('sm')]: {
    gap: '24px'
  }
}));

const HeaderSection = styled(Stack)(({ theme }) => ({
  gap: '6px'
}));

const MainTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '28px',
  fontWeight: 600,
  color: '#0B91D6',
  [theme.breakpoints.down('md')]: {
    fontSize: '24px'
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '20px'
  }
}));

const Description = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 400,
  color: '#30302E',
  lineHeight: '17px'
}));

const SummaryCard = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '30px',
  padding: '26px 26px',
  borderRadius: '12px',
  border: '0.6px solid #F6F0F0',
  backgroundColor: '#ffffff',
  position: 'relative',
  width: '100%',
  overflow: 'hidden',
  [theme.breakpoints.down('lg')]: {
    gap: '24px',
    padding: '20px'
  },
  [theme.breakpoints.down('md')]: {
    flexWrap: 'wrap',
    gap: '16px',
    padding: '16px'
  },
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    gap: '12px',
    padding: '16px'
  }
}));

const SummaryColumn = styled(Stack)(({ theme }) => ({
  gap: '6px',
  flex: 1
}));

const SummaryLabel = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 400,
  color: '#A0A0AA'
}));

const SummaryValue = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 400,
  color: '#121927'
}));

const VerticalDivider = styled(Box)(({ theme }) => ({
  width: '1px',
  height: '95px',
  backgroundColor: '#F6F0F0',
  [theme.breakpoints.down('md')]: {
    display: 'none'
  }
}));

const EditButton = styled(Button)(({ theme }) => ({
  position: 'absolute',
  top: '26px',
  right: '26px',
  minWidth: 'auto',
  padding: '4px',
  color: '#0B91D6',
  '&:hover': {
    backgroundColor: 'transparent'
  }
}));

const ResultsSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '40px',
  padding: '40px 26px',
  borderRadius: '16px',
  border: 'none',
  background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)',
  boxShadow: '0 8px 32px rgba(11, 145, 214, 0.08)',
  alignItems: 'flex-start',
  position: 'relative',
  overflow: 'hidden',
  width: '100%',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: 'linear-gradient(90deg, #0B91D6 0%, #34A853 50%, #FBBC05 100%)'
  },
  [theme.breakpoints.down('lg')]: {
    gap: '32px',
    padding: '32px 20px'
  },
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    gap: '24px',
    padding: '24px 16px',
    alignItems: 'center'
  },
  [theme.breakpoints.down('sm')]: {
    gap: '20px',
    padding: '20px 12px',
    borderRadius: '12px'
  }
}));

const ScoreSection = styled(Stack)(({ theme }) => ({
  alignItems: 'center',
  gap: '16px',
  minWidth: '320px',
  padding: '24px',
  borderRadius: '12px',
  background: 'linear-gradient(135deg, #0B91D6 0%, #0980C2 100%)',
  boxShadow: '0 4px 20px rgba(11, 145, 214, 0.25)',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-50%',
    right: '-50%',
    width: '200%',
    height: '200%',
    background: 'radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%)',
    pointerEvents: 'none'
  },
  [theme.breakpoints.down('md')]: {
    minWidth: '280px',
    width: '100%',
    maxWidth: '400px',
    padding: '20px'
  },
  [theme.breakpoints.down('sm')]: {
    minWidth: 'auto',
    width: '100%',
    padding: '16px'
  }
}));

const ScoreLabel = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 600,
  color: 'rgba(255, 255, 255, 0.9)',
  textAlign: 'center',
  textTransform: 'uppercase',
  letterSpacing: '1px',
  position: 'relative',
  zIndex: 1
}));

const ScoreValue = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '48px',
  fontWeight: 800,
  color: '#ffffff',
  textAlign: 'center',
  lineHeight: 1,
  position: 'relative',
  zIndex: 1,
  textShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
  [theme.breakpoints.down('sm')]: {
    fontSize: '40px'
  }
}));

const ScoreSubtitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '12px',
  fontWeight: 500,
  color: 'rgba(255, 255, 255, 0.8)',
  textAlign: 'center',
  position: 'relative',
  zIndex: 1
}));

const ChartContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '302px',
  height: '301px',
  [theme.breakpoints.down('lg')]: {
    width: '260px',
    height: '260px'
  },
  [theme.breakpoints.down('md')]: {
    width: '240px',
    height: '240px'
  },
  [theme.breakpoints.down('sm')]: {
    width: '200px',
    height: '200px'
  }
}));

const ChartImage = styled('img')(({ theme }) => ({
  width: '100%',
  height: '100%',
  objectFit: 'contain'
}));

const AboutSection = styled(Stack)(({ theme }) => ({
  flex: 1,
  gap: '10px',
  width: '100%',
  [theme.breakpoints.down('md')]: {
    alignItems: 'center',
    textAlign: 'center'
  }
}));

const AboutTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '20px',
  fontWeight: 700,
  color: '#0B91D6',
  marginBottom: '8px',
  [theme.breakpoints.down('md')]: {
    fontSize: '18px'
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '16px'
  }
}));

const AboutText = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '15px',
  fontWeight: 500,
  color: '#30302E',
  lineHeight: '32px',
  marginBottom: '24px',
  '& br': {
    display: 'block',
    content: '""',
    marginTop: '4px'
  },
  [theme.breakpoints.down('md')]: {
    fontSize: '14px',
    lineHeight: '28px'
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '13px',
    lineHeight: '24px',
    marginBottom: '16px'
  }
}));

const FeatureItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '12px 16px',
  borderRadius: '8px',
  backgroundColor: 'rgba(11, 145, 214, 0.04)',
  border: '1px solid rgba(11, 145, 214, 0.1)',
  transition: 'all 0.3s ease',
  marginBottom: '10px',
  width: '100%',
  '&:hover': {
    backgroundColor: 'rgba(11, 145, 214, 0.08)',
    transform: 'translateX(4px)',
    boxShadow: '0 2px 8px rgba(11, 145, 214, 0.15)'
  },
  [theme.breakpoints.down('sm')]: {
    padding: '10px 12px',
    gap: '10px'
  }
}));

const FeatureIcon = styled(Box)(({ theme }) => ({
  width: '32px',
  height: '32px',
  borderRadius: '8px',
  background: 'linear-gradient(135deg, #0B91D6 0%, #34A853 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#ffffff',
  fontSize: '16px',
  fontWeight: 700,
  flexShrink: 0
}));

const FeatureText = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 500,
  color: '#30302E',
  flex: 1,
  [theme.breakpoints.down('sm')]: {
    fontSize: '13px'
  }
}));

const UpgradeSection = styled(Box)(({ theme }) => ({
  marginTop: '32px',
  padding: '24px',
  borderRadius: '12px',
  background: 'linear-gradient(135deg, #0B91D6 0%, #0980C2 100%)',
  boxShadow: '0 4px 20px rgba(11, 145, 214, 0.2)',
  textAlign: 'center',
  width: '100%',
  [theme.breakpoints.down('md')]: {
    marginTop: '24px',
    padding: '20px'
  },
  [theme.breakpoints.down('sm')]: {
    marginTop: '16px',
    padding: '16px'
  }
}));

const UpgradeText = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '16px',
  fontWeight: 600,
  color: '#ffffff',
  marginBottom: '16px',
  lineHeight: '24px',
  [theme.breakpoints.down('md')]: {
    fontSize: '15px'
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '14px',
    marginBottom: '12px',
    lineHeight: '20px'
  }
}));

const UpgradeButton = styled(Button)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 600,
  textTransform: 'none',
  padding: '12px 32px',
  borderRadius: '8px',
  backgroundColor: '#ffffff',
  color: '#0B91D6',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
  '&:hover': {
    backgroundColor: '#f8f9ff',
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
  },
  transition: 'all 0.3s ease',
  [theme.breakpoints.down('sm')]: {
    fontSize: '13px',
    padding: '10px 24px',
    width: '100%'
  }
}));

const LegendContainer = styled(Stack)(({ theme }) => ({
  gap: '16px',
  width: '100%',
  [theme.breakpoints.down('sm')]: {
    gap: '12px'
  }
}));

const LegendItem = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: '12px'
}));

const LegendDot = styled(Box)(({ theme, color }) => ({
  width: '20px',
  height: '20px',
  borderRadius: '50%',
  backgroundColor: color
}));

const LegendText = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '16px',
  fontWeight: 400,
  color: '#121927',
  textTransform: 'capitalize',
  [theme.breakpoints.down('md')]: {
    fontSize: '15px'
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '14px'
  }
}));

const ExportCSVButton = styled(Button)(({ theme }) => ({
  alignSelf: 'center',
  fontFamily: 'Inter, sans-serif',
  fontSize: '13px',
  fontWeight: 600,
  textTransform: 'capitalize',
  width: '140px',
  height: '40px',
  padding: 0,
  borderRadius: '8px',
  backgroundColor: '#0B91D6',
  color: '#ffffff',
  '&:hover': {
    backgroundColor: '#0980C2'
  },
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    maxWidth: '200px',
    fontSize: '12px',
    height: '36px'
  }
}));

// Futuristic Loading UI Components
const pulse = keyframes`
  0% { opacity: 0.6; }
  50% { opacity: 1; }
  100% { opacity: 0.6; }
`;

const slideIn = keyframes`
  0% { transform: translateX(-20px); opacity: 0; }
  100% { transform: translateX(0); opacity: 1; }
`;

const LoadingOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(11, 145, 214, 0.95)',
  backdropFilter: 'blur(10px)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
  borderRadius: '12px'
}));

const LoadingContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '32px',
  maxWidth: '500px',
  width: '100%',
  padding: '40px'
}));

const LoadingTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '28px',
  fontWeight: 700,
  color: '#ffffff',
  textAlign: 'center',
  marginBottom: '8px'
}));

const LoadingSubtitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '16px',
  fontWeight: 400,
  color: 'rgba(255, 255, 255, 0.8)',
  textAlign: 'center'
}));

const ProgressSection = styled(Box)(({ theme }) => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: '20px'
}));

const ProgressBar = styled(LinearProgress)(({ theme }) => ({
  height: '8px',
  borderRadius: '4px',
  backgroundColor: 'rgba(255, 255, 255, 0.2)',
  '& .MuiLinearProgress-bar': {
    borderRadius: '4px',
    background: 'linear-gradient(90deg, #34A853 0%, #0B91D6 50%, #EA4335 100%)',
    backgroundSize: '200% 100%',
    animation: `${pulse} 2s ease-in-out infinite`
  }
}));

const CurrentStepText = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '18px',
  fontWeight: 600,
  color: '#ffffff',
  textAlign: 'center',
  animation: `${slideIn} 0.5s ease-out`
}));

const ProgressPercentage = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '24px',
  fontWeight: 700,
  color: '#ffffff',
  textAlign: 'center'
}));

const StepsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  width: '100%',
  maxHeight: '200px',
  overflowY: 'auto',
  padding: '16px',
  backgroundColor: 'rgba(255, 255, 255, 0.1)',
  borderRadius: '12px',
  backdropFilter: 'blur(5px)'
}));

const StepItem = styled(Box)(({ theme, completed }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '8px 12px',
  borderRadius: '8px',
  backgroundColor: completed ? 'rgba(52, 168, 83, 0.2)' : 'rgba(255, 255, 255, 0.05)',
  border: completed ? '1px solid rgba(52, 168, 83, 0.3)' : '1px solid rgba(255, 255, 255, 0.1)',
  transition: 'all 0.3s ease',
  animation: completed ? `${slideIn} 0.5s ease-out` : 'none'
}));

const StepIcon = styled(Box)(({ theme, completed }) => ({
  width: '20px',
  height: '20px',
  borderRadius: '50%',
  backgroundColor: completed ? '#34A853' : 'rgba(255, 255, 255, 0.2)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '12px',
  color: '#ffffff',
  fontWeight: 'bold'
}));

const StepText = styled(Typography)(({ theme, completed }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: completed ? 600 : 400,
  color: completed ? '#ffffff' : 'rgba(255, 255, 255, 0.7)',
  flex: 1
}));

const legendItems = [];

const predefinedKeywords = [
  'Property',
  'Buy/Rent Property',
  'Builder',
  'Tiles work',
  'Wooden flowing',
  'Civil work'
];

const ProfileStrengthResults = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state || {};
  const account = state.account || {};
  const business = state.business || {};
  const selectedKeywords = Array.isArray(state.keywords) ? state.keywords : [];
  const competitors = state.competitors || [];
  const redirectToDashboard = state?.redirectToDashboard === true;
  const redirectDelayMs = typeof state?.redirectDelayMs === 'number' ? state.redirectDelayMs : 2600;

  // Log competitor data for debugging
  logger.log('[ProfileStrengthResults] Navigation state received:', {
    hasCompetitors: !!state.competitors,
    competitorsLength: competitors.length,
    competitorsData: competitors.slice(0, 3), // Show first 3 competitors
    fullState: Object.keys(state)
  });
  let resolvedAddress = state.address || business.address || business.formattedAddress || '';
  if (!resolvedAddress && business?.storefrontAddress) {
    const sa = business.storefrontAddress;
    const parts = [];
    if (Array.isArray(sa.addressLines) && sa.addressLines.length) parts.push(sa.addressLines.join(', '));
    if (sa.locality) parts.push(sa.locality);
    const tail = [sa.administrativeArea, sa.postalCode].filter(Boolean).join(' ');
    if (tail) parts.push(tail);
    resolvedAddress = parts.join(', ').trim();
  }
  const resolvedAccountName = account.name || business.name || business.businessName || '';
  const [freshAddress, setFreshAddress] = useState('');
  const [targetLocation, setTargetLocation] = useState(business || state.locationData || state.selectedLocation || null);
  const [profileScore, setProfileScore] = useState(0);
  const [keywordsState, setKeywordsState] = useState(selectedKeywords);
  const [editKwOpen, setEditKwOpen] = useState(false);
  const [manualKw, setManualKw] = useState('');
  // Plan-based keyword limit: basic=2, advance=5, pro=10
  const [currentPlan, setCurrentPlan] = useState('pro');
  const [keywordLimit, setKeywordLimit] = useState(10); // Default to pro limit
  // Additional datasets to mirror Business Profile page
  const [reviewsData, setReviewsData] = useState(null);
  const [mediaItems, setMediaItems] = useState([]);
  const [performanceData, setPerformanceData] = useState(null);

  // Loading states for attractive UI
  const [isCalculating, setIsCalculating] = useState(() => {
    logger.log('[ProfileStrengthResults] Initializing isCalculating state');
    logger.log('[ProfileStrengthResults] state object:', state);
    logger.log('[ProfileStrengthResults] state.startAnalysis:', state?.startAnalysis);
    const shouldStart = state?.startAnalysis === true;
    logger.log('[ProfileStrengthResults] Initial isCalculating value:', shouldStart);
    return shouldStart;
  });
  const [currentStep, setCurrentStep] = useState('');
  const [completedSteps, setCompletedSteps] = useState([]);

  // Check if current user is admin
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    try {
      const userDataStr = localStorage.getItem('userData');
      if (userDataStr) {
        const userData = JSON.parse(userDataStr);
        setIsAdmin(userData.role_id === 9);
      }
    } catch (error) {
      logger.error('[ProfileStrengthResults] Error checking admin status:', error);
      setIsAdmin(false);
    }
  }, []);
  const [stepProgress, setStepProgress] = useState(0);
  const [scoreBreakdown, setScoreBreakdown] = useState([]);
  // User preference: near-instant analysis toggle
  const [fastAnalysis, setFastAnalysis] = useState(true);

  // Fetch plan for keyword limit
  useEffect(() => {
    const fetchPlan = async () => {
      try {
        // Extract location ID from various sources
        let locationId = null;
        if (typeof business?.name === 'string' && business.name.includes('locations/')) {
          locationId = business.name.split('/').pop();
        } else if (business?.locationId) {
          locationId = business.locationId;
        } else if (typeof state?.locationData?.name === 'string' && state.locationData.name.includes('locations/')) {
          locationId = state.locationData.name.split('/').pop();
        } else if (state?.locationData?.locationId) {
          locationId = state.locationData.locationId;
        }

        if (!locationId) {
          logger.log('[ProfileStrengthResults] No location ID found, using pro defaults');
          return;
        }

        // Add timestamp to prevent caching
        const result = await ApiService.get(`/admin/gmb-account/plan?location_id=${encodeURIComponent(locationId)}&_t=${Date.now()}`);

        let plan = 'pro';
        if (result?.success && result?.data?.plan) {
          plan = result.data.plan;
        } else if (result?.plan) {
          plan = result.plan;
        }

        setCurrentPlan(plan);
        setKeywordLimit(getKeywordTrackingLimit(plan));
        logger.log(`[ProfileStrengthResults] Plan: ${plan}, Keyword Limit: ${getKeywordTrackingLimit(plan)}`);
      } catch (error) {
        logger.error('[ProfileStrengthResults] Error fetching plan:', error);
        // Default to pro on error
        setCurrentPlan('pro');
        setKeywordLimit(10);
      }
    };

    // Fetch on mount
    fetchPlan();

    // Refetch plan when tab becomes visible (user might have changed plan in admin)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchPlan();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Also refetch every 30 seconds
    const interval = setInterval(fetchPlan, 30000);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(interval);
    };
  }, [business, state?.locationData]);

  // Controls the results popup that shows the animated gauge once analysis completes
  // Track if we've already processed the startAnalysis flag to prevent infinite loops
  const [hasProcessedStartFlag, setHasProcessedStartFlag] = useState(() => {
    const initial = state?.startAnalysis === true;
    logger.log('[ProfileStrengthResults] Initial hasProcessedStartFlag:', initial);
    return initial;
  });

  // When analysis finishes (isCalculating -> false), open the popup and animate the gauge
  useEffect(() => {
    if (!redirectToDashboard) return;
    if (isCalculating) return;

    const timeout = setTimeout(() => {
      navigate('/dashboard', {
        replace: true,
        state: {
          from: 'profile-strength',
          profileScore,
        },
      });
    }, Math.max(redirectDelayMs, 1000));

    return () => clearTimeout(timeout);
  }, [redirectToDashboard, isCalculating, redirectDelayMs, profileScore, navigate]);

  // Ensure target location is always set with available data
  useEffect(() => {
    if (!targetLocation && (business || state.locationData || state.selectedLocation)) {
      const fallbackLocation = business || state.locationData || state.selectedLocation;
      logger.log('[ProfileStrengthResults] Setting fallback target location:', fallbackLocation);
      setTargetLocation(fallbackLocation);
    }
  }, [business, state.locationData, state.selectedLocation, targetLocation]);

  useEffect(() => {
    const fetchFreshAddress = async () => {
      try {
        logger.log('[ProfileStrengthResults] Starting data fetch with state:', {
          account,
          business,
          selectedKeywords,
          resolvedAddress,
          state
        });

        // Derive account id/name from multiple sources
        let accId = account?.id;
        if (!accId) accId = business?.accountId || business?.accountName;
        if (!accId && typeof business?.name === 'string' && business.name.includes('accounts/')) {
          // name might look like accounts/123/locations/456
          const parts = String(business.name).split('/');
          const idx = parts.indexOf('accounts');
          if (idx !== -1 && parts[idx + 1]) accId = parts[idx + 1];
        }
        if (!accId && Array.isArray(state.businesses)) {
          const withAcc = state.businesses.find(b => b?.accountId || b?.accountName || (typeof b?.name === 'string' && b.name.includes('accounts/')));
          if (withAcc) {
            accId = withAcc.accountId || withAcc.accountName;
            if (!accId && typeof withAcc.name === 'string' && withAcc.name.includes('accounts/')) {
              const parts = String(withAcc.name).split('/');
              const idx = parts.indexOf('accounts');
              if (idx !== -1 && parts[idx + 1]) accId = parts[idx + 1];
            }
          }
        }
        // derive location id from possible fields
        const locId = account?.locationId || business?.locationId || business?.id || (business?.name ? String(business.name).split('/').pop() : '');

        logger.log('[ProfileStrengthResults] Derived IDs:', { accId, locId });

        if (!accId) {
          logger.warn('[Results] No account id derived initially. Attempting discovery via accounts â†’ locations...');
          try {
            const accountsList = await GMBService.getAccounts();
            logger.debug('[Results] Accounts fetched for discovery:', Array.isArray(accountsList) ? accountsList.length : 0);
            let discoveredAccId = undefined;
            if (Array.isArray(accountsList)) {
              for (const acc of accountsList) {
                const accName = acc?.name; // 'accounts/<id>'
                const accIdCandidate = typeof accName === 'string' && accName.includes('accounts/') ? accName.split('/')[1] : undefined;
                if (!accIdCandidate) continue;
                try {
                  const locs = await GMBService.getLocations(undefined, accName);
                  const byId = (loc) => (loc?.name && (loc.name.includes(String(locId)) || String(loc.name).split('/').pop() === String(locId)));
                  const byTitle = (loc) => {
                    const selName = business?.title || business?.businessName || business?.name || '';
                    return selName && loc?.title && String(loc.title).toLowerCase() === String(selName).toLowerCase();
                  };
                  const match = Array.isArray(locs) ? (locs.find(byId) || locs.find(byTitle)) : null;
                  if (match) {
                    discoveredAccId = accIdCandidate;
                    logger.debug('[Results] Discovered accountId via location match:', discoveredAccId, 'location:', match?.name);
                    // Prefer to use discovered accId and continue normal flow to keep logic unified
                    accId = discoveredAccId;
                    break;
                  }
                } catch (locErr) {
                  logger.warn('[Results] Failed to list locations for', accName, locErr?.message || locErr);
                }
              }
            }
          } catch (discErr) {
            logger.warn('[Results] Account discovery failed:', discErr?.message || discErr);
          }

          if (!accId) {
            logger.warn('[Results] Could not determine account id after discovery. Falling back to passed business payload.');
            // Still try to set target location from business data for scoring
            if (business) {
              logger.log('[Results] Setting target location from business data for scoring');
              setTargetLocation(business);
            }
            return;
          }
        }
        const accountName = String(accId).includes('accounts/') ? String(accId) : `accounts/${accId}`;
        logger.debug('[Results] Fetching locations for', accountName, 'target locId:', locId);
        const locations = await GMBService.getLocations(undefined, accountName);
        logger.debug('[Results] Locations fetched:', Array.isArray(locations) ? locations.length : 0);
        let target = null;
        if (Array.isArray(locations) && locations.length) {
          if (locId) {
            target = locations.find(l => (l.name && (l.name.includes(locId) || String(l.name).split('/').pop() === String(locId)))) || null;
          }
          if (!target) {
            // Try match by title/name
            const selName = business?.title || business?.businessName || business?.name || '';
            if (selName) {
              target = locations.find(l => (l.title && String(l.title).toLowerCase() === String(selName).toLowerCase()));
            }
          }
          if (!target) {
            target = locations[0];
          }
        }
        logger.debug('[Results] Target location chosen:', target?.name || 'none');
        if (target) {
          setTargetLocation(target);
          // Fetch attributes for this location and attach for scoring
          try {
            if (target?.name) {
              const attrs = await GMBService.getLocationAttributes(target.name);
              logger.debug('[Results] Attributes fetched:', Array.isArray(attrs) ? attrs.length : 0);
              setTargetLocation((prev) => ({ ...prev, attributesApi: attrs }));
            }
          } catch (e) {
            logger.warn('[Results] Failed to fetch attributes:', e?.message || e);
          }

          // Fetch additional datasets to mirror BusinessProfile page
          try {
            const accountId = (accountName && accountName.includes('accounts/')) ? accountName.split('/')[1] : undefined;
            const locationName = target?.name || '';
            const locationIdFromName = locationName ? String(locationName).split('/').pop() : undefined;

            // Reviews (uses accountName and full location name)
            try {
              if (accountName && locationName) {
                const reviews = await GMBService.getReviews(undefined, accountName, locationName);
                setReviewsData({ reviews });
                logger.debug('[Results] Reviews fetched:', Array.isArray(reviews) ? reviews.length : 0);
              }
            } catch (reviewsErr) {
              logger.warn('[Results] Error fetching reviews:', reviewsErr);
              setReviewsData({ reviews: [] });
            }

            // Media (photos) requires accountId and locationId
            try {
              if (accountId && locationIdFromName) {
                logger.debug('[Results] Fetching media with', { accountId, locationIdFromName });
                // Fetch all media with default pageSize=50 (now paginated internally)
                let items = await GMBService.getMedia(undefined, accountId, locationIdFromName, 50);
                items = Array.isArray(items) ? items : [];
                // Filter to photos with a usable URL
                items = items.filter(i => (i.mediaFormat === 'PHOTO' || !i.mediaFormat) && (i.googleUrl || i.thumbnailUrl || i.sourceUrl));
                // Sort by createTime desc
                items.sort((a, b) => {
                  const ta = a.createTime ? Date.parse(a.createTime) : 0;
                  const tb = b.createTime ? Date.parse(b.createTime) : 0;
                  return tb - ta;
                });
                setMediaItems(items);
                const sample = items.slice(0, 3).map(i => ({ name: i.name, format: i.mediaFormat, created: i.createTime, url: i.googleUrl || i.thumbnailUrl || i.sourceUrl }));
                // Also compute total photo count via dedicated counter to validate versus items length
                const totalPhotoCount = await GMBService.getMediaCount(undefined, accountId, locationIdFromName, 50);
                logger.debug('[Results] Media items fetched (after pagination/filter/sort):', items.length, 'sample:', sample, 'totalPhotoCount:', totalPhotoCount);
              }
            } catch (mediaErr) {
              logger.warn('[Results] Error fetching media:', mediaErr);
              setMediaItems([]);
            }

            // Performance metrics (locationId only)
            try {
              if (locationIdFromName) {
                const metrics = await GMBService.getPerformanceMetrics(undefined, locationIdFromName);
                setPerformanceData(metrics);
                logger.debug('[Results] Performance metrics fetched');
              }
            } catch (perfErr) {
              logger.warn('[Results] Error fetching performance metrics:', perfErr);
              setPerformanceData(null);
            }

          } catch (extraErr) {
            logger.warn('[Results] Error during extra datasets fetch:', extraErr);
          }
        }
        if (!target?.latlng?.latitude || !target?.latlng?.longitude) {
          logger.debug('[Results] Target location lat/lng missing or incomplete. latlng field:', target?.latlng);
        } else {
          logger.debug('[Results] Target location coordinates:', {
            lat: target?.latlng?.latitude,
            lng: target?.latlng?.longitude
          });
        }
        if (target?.storefrontAddress) {
          const sa = target.storefrontAddress;
          const parts = [];
          if (Array.isArray(sa.addressLines) && sa.addressLines.length) parts.push(sa.addressLines.join(', '));
          if (sa.locality) parts.push(sa.locality);
          const tail = [sa.administrativeArea, sa.postalCode].filter(Boolean).join(' ');
          if (tail) parts.push(tail);
          const formatted = parts.join(', ').trim();
          logger.debug('[Results] Fresh address derived from API:', formatted);
          setFreshAddress(formatted);
        }
      } catch (e) {
        logger.warn('Could not fetch fresh address:', e);
      }
    };
    fetchFreshAddress();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account?.id, business?.locationId, business?.id, business?.name]);

  // Fallback: try to pull an address from the passed businesses list
  let fallbackBusinessesAddress = '';
  if (!freshAddress && !resolvedAddress && Array.isArray(state.businesses) && state.businesses.length) {
    const locId = business?.locationId || business?.id || (business?.name ? String(business.name).split('/').pop() : '');
    const matchesSelected = state.businesses.find(b => {
      const bid = b?.locationId || b?.id || (b?.name ? String(b.name).split('/').pop() : '');
      return locId && bid && String(bid) === String(locId);
    });
    const candidate = matchesSelected || state.businesses.find(b => b?.address || b?.formattedAddress || b?.storefrontAddress);
    if (candidate) {
      if (candidate.storefrontAddress) {
        const sa = candidate.storefrontAddress;
        const parts = [];
        if (Array.isArray(sa.addressLines) && sa.addressLines.length) parts.push(sa.addressLines.join(', '));
        if (sa.locality) parts.push(sa.locality);
        const tail = [sa.administrativeArea, sa.postalCode].filter(Boolean).join(' ');
        if (tail) parts.push(tail);
        fallbackBusinessesAddress = parts.join(', ').trim();
      } else {
        fallbackBusinessesAddress = candidate.address || candidate.formattedAddress || '';
      }
    }
  }
  const displayAddress = (freshAddress || resolvedAddress || fallbackBusinessesAddress || '').trim();
  useEffect(() => {
    const chosen = freshAddress ? 'freshAddress' : (resolvedAddress ? 'resolvedAddress' : (fallbackBusinessesAddress ? 'fallbackBusinessesAddress' : 'none'));
    logger.debug('[Results] Address source used:', chosen, 'value:', displayAddress);
  }, [freshAddress, resolvedAddress, fallbackBusinessesAddress, displayAddress]);

  // Compute profile strength score out of 300 based on provided rules
  useEffect(() => {
    logger.log('[ProfileStrengthResults] Main analysis useEffect triggered');
    logger.log('[ProfileStrengthResults] Current isCalculating:', isCalculating);
    logger.log('[ProfileStrengthResults] Dependencies:', {
      verificationState: account?.verificationState,
      targetLocation: !!targetLocation,
      displayAddress,
      keywordsState,
      businessTitle: business?.title,
      businessName: business?.businessName,
      startAnalysis: state?.startAnalysis
    });

    let isMounted = true;
    // Speed control for UX: lower = faster analysis (0 = instant)
    const speedFactor = fastAnalysis ? 0 : 0.25; // instant vs fast
    const wait = (ms) => new Promise((resolve) => setTimeout(resolve, Math.max(0, Math.floor(ms * speedFactor))));
    const computeScore = async () => {
      logger.log('[ProfileStrengthResults] computeScore function called');
      if (!isMounted) return;
      setIsCalculating(true);
      setCurrentStep('Initializing analysis...');
      setStepProgress(0);
      await wait(800); // Brief pause for UX
      let score = 0;
      let verPts = 0, namePts = 0, addrPts = 0, phonePts = 0;

      const updateProgress = (step, progress, completed = []) => {
        if (!isMounted) return;
        setCurrentStep(step);
        setStepProgress(progress);
        setCompletedSteps(prev => [...new Set([...prev, ...completed])]);
      };

      // 1) Verification status (respect Dashboard-passed status/flags)
      updateProgress('Checking verification status...', 10);
      await wait(600);
      const verificationStateRaw = account?.verificationState || '';
      const verificationState = String(verificationStateRaw).toUpperCase();
      const dashStatus = String(business?.status || state?.selected?.status || '').toLowerCase();
      const dashVerified = (business?.verified === true) || (state?.selected?.verified === true);
      const isVerified = (
        dashVerified ||
        dashStatus === 'verified' ||
        verificationState === 'VERIFIED' ||
        String(verificationStateRaw).toLowerCase() === 'verified' ||
        targetLocation?.metadata?.verified === true
      );
      try { logger.debug('[Results] Verification inputs:', { verificationStateRaw, verificationState, dashStatus, dashVerified, resolvedIsVerified: isVerified }); } catch (_) { }
      const isSoftSuspended = (
        (targetLocation?.metadata?.placeSuspensionReason || '').toString().toUpperCase().includes('SOFT') ||
        (targetLocation?.metadata?.state || '').toString().toUpperCase().includes('SOFT')
      );
      const isUnverified = !isVerified && !isSoftSuspended;
      if (isVerified) verPts = 0;
      else if (isSoftSuspended) verPts = -60;
      else if (isUnverified) verPts = -100; // not specified; assuming 0
      score += verPts;
      updateProgress('Analyzing business name...', 20, ['Verification Status']);
      await wait(500);

      // 2) Business name contains city
      const businessTitle = targetLocation?.title || business?.title || business?.businessName || '';
      const city = targetLocation?.storefrontAddress?.locality || '';
      if (businessTitle && city && businessTitle.toLowerCase().includes(city.toLowerCase())) {
        namePts = 20;
      } else if (businessTitle) {
        namePts = 10;
      }
      score += namePts;
      updateProgress('Evaluating address completeness...', 35, ['Business Name Analysis']);
      await wait(600);

      // 3) Address scoring
      const addr = displayAddress;
      const hasCity = !!city && addr.toLowerCase().includes(city.toLowerCase());
      const keywords = (keywordsState && keywordsState.length ? keywordsState : [])
        .map(k => k.toLowerCase());
      const hasKeyword = keywords.some(k => addr.toLowerCase().includes(k));
      const hasAnyAddressFields = !!(targetLocation?.storefrontAddress?.locality || targetLocation?.storefrontAddress?.administrativeArea || targetLocation?.storefrontAddress?.postalCode);
      if (hasCity && hasKeyword) {
        addrPts = 20;
      } else if (addr) {
        addrPts = 12;
      } else if (!hasAnyAddressFields) {
        addrPts = 2;
      }
      score += addrPts;
      updateProgress('Checking contact information...', 45, ['Address Analysis']);
      await wait(400);

      // 4) Phone number with uniqueness check
      const primaryPhone = targetLocation?.phoneNumbers?.primaryPhone ||
        targetLocation?.nationalPhoneNumber ||
        targetLocation?.internationalPhoneNumber ||
        business?.phoneNumber ||
        business?.phone;

      let phoneScoreResult = { score: 0, analysis: { hasPhone: false } };

      if (primaryPhone) {
        try {
          logger.log('[ProfileStrength] Starting phone validation for:', primaryPhone);
          // Get current business place ID for exclusion
          const currentPlaceId = targetLocation?.metadata?.placeId ||
            targetLocation?.placeId ||
            business?.placeId ||
            business?.place_id ||
            state?.locationData?.placeId ||
            state?.selectedLocation?.placeId;

          logger.log('[ProfileStrength] Using place ID for exclusion:', currentPlaceId);

          // Add timeout to prevent hanging
          const phoneValidationPromise = PhoneValidationService.calculatePhoneScore(primaryPhone, currentPlaceId);
          const timeoutPromise = new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Phone validation timeout')), 10000) // 10 second timeout
          );

          phoneScoreResult = await Promise.race([phoneValidationPromise, timeoutPromise]);
          logger.log('[ProfileStrength] Phone validation result:', phoneScoreResult);
        } catch (error) {
          logger.warn('[ProfileStrength] Phone validation failed, using fallback scoring:', error);
          // Fallback to simple phone presence check
          phoneScoreResult = {
            score: 18,
            analysis: {
              hasPhone: true,
              isUnique: true,
              reason: 'Phone validation unavailable, assuming unique',
              error: error.message
            }
          };
        }
      } else {
        logger.log('[ProfileStrength] No phone number found, skipping phone validation');
      }

      phonePts = phoneScoreResult.score;
      score += phonePts;
      updateProgress('Analyzing business description...', 55, ['Contact Information']);
      await wait(500);

      // 5) Description length
      let descPts = 0;
      const description = targetLocation?.profile?.description || '';
      const wordCount = description ? (description.trim().split(/\s+/).filter(Boolean).length) : 0;
      if (wordCount > 0 && wordCount <= 400) {
        descPts = 5;
      } else if (wordCount >= 700) {
      } else {
        descPts = 0; // between 401 and 699, or empty
      }
      score += descPts;
      updateProgress('Checking website presence...', 65, ['Description Analysis']);
      await wait(200); // Speed up analysis by reducing wait time

      // 6) Website link presence
      let websitePts = 0;
      const hasWebsite = !!targetLocation?.websiteUri;
      websitePts = hasWebsite ? 5 : 0;
      score += websitePts;
      updateProgress('Evaluating business hours...', 72, ['Website Analysis']);
      await wait(225); // Speed up analysis by reducing wait time

      // 7) Business hours (timings)
      let hoursPts = 0;
      const hasHours = !!(targetLocation?.regularHours?.periods?.length || targetLocation?.moreHours?.length);
      hoursPts = hasHours ? 5 : 0;
      score += hoursPts;
      updateProgress('Checking business labels...', 78, ['Business Hours']);
      await wait(200); // Speed up analysis by reducing wait time

      // 8) Labels
      let labelPts = 0;
      const hasLabels = Array.isArray(targetLocation?.labels) && targetLocation.labels.length > 0;
      labelPts = hasLabels ? 10 : 0;
      score += labelPts;
      updateProgress('Analyzing business categories...', 85, ['Labels Analysis']);
      await wait(250); // Speed up analysis by reducing wait time

      // 8.0) Categories scoring: +25 for primary, +5 for first additional, +5 for second additional
      let catBasePts = 0;
      let catExtraPts = 0;
      const catInfo = targetLocation?.categories || {};
      const primaryCategory = catInfo?.primaryCategory || catInfo?.primary || null;
      // additionalCategories in Business Info API; also guard for arrays under different keys
      const additionalCats = Array.isArray(catInfo?.additionalCategories)
        ? catInfo.additionalCategories
        : (Array.isArray(catInfo)
          ? catInfo.slice(1) // if categories is an array, assume first is primary
          : []);
      const hasPrimary = !!primaryCategory;
      if (hasPrimary) catBasePts = 25;
      const extraCount = Math.max(0, Math.min(Array.isArray(additionalCats) ? additionalCats.length : 0, 2));
      catExtraPts = extraCount * 5; // +5 for first extra, +5 for second extra
      score += (catBasePts + catExtraPts);
      logger.log('[Score] Categories check:', {
        hasPrimary,
        additionalCount: Array.isArray(additionalCats) ? additionalCats.length : 0,
        awardedPrimary: catBasePts,
        awardedAdditional: catExtraPts
      });

      // 8.0a) Category mentions locality area via external API (+10)
      let catCityPts = 0;
      let hasCityInCategory = false;
      const businessName = businessTitle || resolvedAccountName || '';

      // Define extractCatNames function for reuse
      const extractCatNames = () => {
        const names = [];
        const pushStr = (v) => { if (typeof v === 'string' && v.trim()) names.push(v); };
        if (primaryCategory) {
          pushStr(primaryCategory.displayName || primaryCategory.name || primaryCategory);
        }
        (Array.isArray(additionalCats) ? additionalCats : []).forEach((c) => {
          pushStr(c?.displayName || c?.name || c);
        });
        return names;
      };

      if (businessName) {
        try {
          updateProgress('Checking category locality...', 87, ['Categories & Services']);
          await wait(300);

          const apiUrl = `https://api.visibeen.com/api/v1/customer/profile/get-business-category?businessName=${encodeURIComponent(businessName)}`;
          const response = await fetch(apiUrl);
          const result = await response.json();

          if (result.code === 200 && result.data && Array.isArray(result.data.categories)) {
            const categories = result.data.categories;
            const locality = targetLocation?.storefrontAddress?.locality || city || '';
            const administrativeArea = targetLocation?.storefrontAddress?.administrativeArea || '';

            // Check if any category contains locality (city) - award 10 points
            // If only administrative area (state/province) - award 0 points
            const hasLocalityInCategory = locality && categories.some(cat =>
              typeof cat === 'string' && cat.toLowerCase().includes(locality.toLowerCase())
            );

            hasCityInCategory = hasLocalityInCategory;
            catCityPts = hasLocalityInCategory ? 10 : 0;

            logger.log('[Score] External API category-locality check:', {
              businessName,
              categories,
              locality,
              administrativeArea,
              hasLocalityInCategory,
              awarded: catCityPts
            });
          } else {
            logger.warn('[Score] External API failed or returned no categories:', result);
            // Fallback to original logic
            const norm = (s) => (typeof s === 'string' ? s.toLowerCase() : '');
            const cityLc = norm(city);
            const catNames = extractCatNames();
            hasCityInCategory = !!(cityLc && catNames.some(n => norm(n).includes(cityLc)));
            catCityPts = hasCityInCategory ? 10 : 0;
            logger.log('[Score] Fallback city-in-category check:', { city, categoryNames: catNames, awarded: catCityPts });
          }
        } catch (error) {
          logger.error('[Score] External API error, using fallback:', error);
          // Fallback to original logic
          const norm = (s) => (typeof s === 'string' ? s.toLowerCase() : '');
          const cityLc = norm(city);
          const catNames = extractCatNames();
          hasCityInCategory = !!(cityLc && catNames.some(n => norm(n).includes(cityLc)));
          catCityPts = hasCityInCategory ? 10 : 0;
          logger.log('[Score] Error fallback city-in-category check:', { city, categoryNames: catNames, awarded: catCityPts });
        }
      }

      score += catCityPts;

      // 8.1) Social media attached (+5 if present)
      let socialPts = 0;
      const socialDomains = ['facebook.com', 'instagram.com', 'twitter.com', 'x.com', 'linkedin.com', 'youtube.com', 't.me', 'telegram.me', 'pinterest.', 'tiktok.com'];
      const looksSocialUrl = (u) => {
        if (!u || typeof u !== 'string') return false;
        const s = u.toLowerCase();
        return socialDomains.some(d => s.includes(d));
      };
      const collectLinks = () => {
        const out = [];
        const tl = targetLocation || {};
        const candidates = [];
        if (Array.isArray(tl.socialLinks)) candidates.push(...tl.socialLinks);
        if (Array.isArray(tl?.profile?.socialLinks)) candidates.push(...tl.profile.socialLinks);
        if (Array.isArray(tl.links)) candidates.push(...tl.links);
        if (Array.isArray(tl.profiles)) candidates.push(...tl.profiles);
        if (Array.isArray(tl.additionalUrls)) candidates.push(...tl.additionalUrls);
        if (Array.isArray(tl?.profile?.urls)) candidates.push(...tl.profile.urls);
        if (tl.websiteUri) candidates.push(tl.websiteUri);
        // Some structures might be objects with {type, url}
        candidates.forEach((v) => {
          if (!v) return;
          if (typeof v === 'string') out.push(v);
          else if (v?.url) out.push(v.url);
          else if (v?.link) out.push(v.link);
        });
        // Attributes may embed links
        const attrs = tl.attributes || {};
        Object.keys(attrs).forEach((k) => {
          const val = attrs[k];
          if (typeof val === 'string') out.push(val);
          if (Array.isArray(val)) val.forEach(x => { if (typeof x === 'string') out.push(x); });
        });
        // Also scan attributesApi array (Business Info API)
        const attrsApi = Array.isArray(tl.attributesApi) ? tl.attributesApi : [];
        attrsApi.forEach((a) => {
          // push any string-like fields commonly present
          ['displayName', 'attributeId', 'valueType'].forEach(k => {
            if (a && typeof a[k] === 'string') out.push(a[k]);
          });
          // inspect a.values which may contain strings/urls
          const vs = a?.values;
          if (vs && typeof vs === 'object') {
            Object.values(vs).forEach(v => {
              if (typeof v === 'string') out.push(v);
              if (Array.isArray(v)) v.forEach(x => { if (typeof x === 'string') out.push(x); });
            });
          }
        });
        return out.filter(Boolean);
      };
      const allLinks = collectLinks();
      const hasSocial = allLinks.some(looksSocialUrl);
      socialPts = hasSocial ? 5 : 0;
      score += socialPts;
      logger.log('[Score] Social media check:', { hasSocial, awarded: socialPts });

      // 8.2) Appointments link (+5 if present)
      let appointmentsPts = 0;
      const hasApptDirect = !!(
        targetLocation?.appointmentLinks ||
        targetLocation?.appointmentLink ||
        targetLocation?.appointmentsLink ||
        targetLocation?.profile?.appointmentLink ||
        targetLocation?.profile?.appointmentUrl ||
        targetLocation?.metadata?.appointmentLink
      );
      const attrs = targetLocation?.attributes || {};
      const attrKeys = Object.keys(attrs).map(k => k.toLowerCase());
      const hasApptAttr = attrKeys.some(k => k.includes('appointment') || k.includes('booking'));
      const hasApptUrlInAttrs = Object.values(attrs).some(v => {
        if (typeof v === 'string') return /http(s)?:\/\//i.test(v) && (v.toLowerCase().includes('book') || v.toLowerCase().includes('appoint'));
        if (Array.isArray(v)) return v.some(x => typeof x === 'string' && /http(s)?:\/\//i.test(x) && (x.toLowerCase().includes('book') || x.toLowerCase().includes('appoint')));
        return false;
      });
      // Also scan attributesApi array for appointment/booking hints
      const attrsApi = Array.isArray(targetLocation?.attributesApi) ? targetLocation.attributesApi : [];
      const hasApptInAttrsApi = attrsApi.some(a => {
        const strings = [];
        if (a && typeof a.displayName === 'string') strings.push(a.displayName.toLowerCase());
        if (a && typeof a.attributeId === 'string') strings.push(a.attributeId.toLowerCase());
        // collect string values from a.values
        const vs = a?.values;
        if (vs && typeof vs === 'object') {
          Object.values(vs).forEach(v => {
            if (typeof v === 'string') strings.push(v.toLowerCase());
            if (Array.isArray(v)) v.forEach(x => { if (typeof x === 'string') strings.push(x.toLowerCase()); });
          });
        }
        return strings.some(s => s.includes('appointment') || s.includes('book') || (/http(s)?:\/\//i.test(s) && (s.includes('appoint') || s.includes('book'))));
      });
      const hasAppt = !!(hasApptDirect || hasApptAttr || hasApptUrlInAttrs || hasApptInAttrsApi);
      appointmentsPts = hasAppt ? 5 : 0;
      score += appointmentsPts;
      logger.log('[Score] Appointments check:', { hasAppt, awarded: appointmentsPts });

      // 8.3) Service area (+5 if present)
      let serviceAreaPts = 0;
      const sa = targetLocation?.serviceArea || null;
      const hasServiceArea = !!(
        sa && (
          (Array.isArray(sa?.places) && sa.places.length > 0) ||
          sa?.placeId || sa?.radius || sa?.businessArea || sa?.regionCode ||
          Object.keys(sa || {}).length > 0
        )
      );
      serviceAreaPts = hasServiceArea ? 5 : 0;
      score += serviceAreaPts;
      logger.log('[Score] Service area check:', { hasServiceArea, awarded: serviceAreaPts });

      // 8.4) Book Appointment explicit (+5 if present)
      let bookApptPts = 0;
      const linkLooksBook = (u) => typeof u === 'string' && /http(s)?:\/\//i.test(u) && /(book|reserve|booking)/i.test(u);
      const hasBookByLinks = Array.isArray(allLinks) && allLinks.some(linkLooksBook);
      const hasBookByFields = !!(
        targetLocation?.appointmentLinks ||
        targetLocation?.appointmentLink ||
        targetLocation?.appointmentsLink ||
        targetLocation?.profile?.appointmentLink ||
        targetLocation?.profile?.appointmentUrl ||
        targetLocation?.metadata?.appointmentLink
      );
      // look through attributesApi strings for book/reserve terms
      const attrsApiForBook = Array.isArray(targetLocation?.attributesApi) ? targetLocation.attributesApi : [];
      const hasBookInAttrsApi = attrsApiForBook.some(a => {
        const strs = [];
        if (a && typeof a.displayName === 'string') strs.push(a.displayName);
        if (a && typeof a.attributeId === 'string') strs.push(a.attributeId);
        const vs = a?.values;
        if (vs && typeof vs === 'object') {
          Object.values(vs).forEach(v => {
            if (typeof v === 'string') strs.push(v);
            if (Array.isArray(v)) v.forEach(x => { if (typeof x === 'string') strs.push(x); });
          });
        }
        return strs.some(s => /(book|reserve|appointment)/i.test(s));
      });
      const hasBookAppointment = !!(hasBookByLinks || hasBookByFields || hasBookInAttrsApi);
      bookApptPts = hasBookAppointment ? 5 : 0;
      score += bookApptPts;
      logger.log('[Score] Book appointment check:', { hasBookAppointment, awarded: bookApptPts });

      // 8.5) Q&A section present (+5 if present)
      let qaPts = 0;
      const qnaCandidates = [
        targetLocation?.qna,
        targetLocation?.qa,
        targetLocation?.questions,
        targetLocation?.questionsAndAnswers,
        targetLocation?.communityQuestions
      ];
      const hasQAByStructure = qnaCandidates.some(v => Array.isArray(v) ? v.length > 0 : (v && typeof v === 'object' && Object.keys(v).length > 0));
      // Also scan attributesApi strings for Q&A hints
      const hasQAInAttrsApi = attrsApiForBook.some(a => {
        const strs = [];
        if (a && typeof a.displayName === 'string') strs.push(a.displayName);
        if (a && typeof a.attributeId === 'string') strs.push(a.attributeId);
        const vs = a?.values;
        if (vs && typeof vs === 'object') {
          Object.values(vs).forEach(v => {
            if (typeof v === 'string') strs.push(v);
            if (Array.isArray(v)) v.forEach(x => { if (typeof x === 'string') strs.push(x); });
          });
        }
        return strs.some(s => /(q&a|questions?\s*&?\s*answers?|community\s*qa)/i.test(s));
      });
      const hasQASection = !!(hasQAByStructure || hasQAInAttrsApi);
      qaPts = hasQASection ? 5 : 0;
      score += qaPts;
      logger.log('[Score] Q&A check:', { hasQASection, awarded: qaPts });

      // 8.6) Photos scoring (+30 max points)
      let photosPts = 0;
      updateProgress('Analyzing business photos...', 82, ['Q&A Analysis']);
      await wait(600);

      // Determine if business is hotel/restaurant type
      const isHotelRestaurant = () => {
        const catNames = extractCatNames().map(n => n.toLowerCase());
        const hotelRestaurantKeywords = [
          'hotel', 'motel', 'resort', 'inn', 'lodge', 'bed and breakfast', 'hostel',
          'restaurant', 'cafe', 'bar', 'pub', 'bistro', 'diner', 'eatery', 'food',
          'pizza', 'burger', 'sushi', 'chinese', 'italian', 'mexican', 'indian',
          'bakery', 'coffee', 'fast food', 'fine dining', 'casual dining'
        ];
        return catNames.some(cat => hotelRestaurantKeywords.some(keyword => cat.includes(keyword)));
      };

      const isHotelRestaurantBusiness = isHotelRestaurant();

      // Fetch photos from GMB Media API
      let photos = [];
      let hasCoverPhoto = false;
      let hasLogoPhoto = false;
      let hasMenuPhotos = false;
      let totalPhotos = 0;

      // Get account and location IDs for media API call
      // Extract accountId from targetLocation (which should have been set by the earlier account discovery logic)
      let accountId = undefined;
      if (targetLocation?.name && targetLocation.name.includes('accounts/')) {
        const parts = String(targetLocation.name).split('/');
        const idx = parts.indexOf('accounts');
        if (idx !== -1 && parts[idx + 1]) accountId = parts[idx + 1];
      }

      // Fallback to original account/business data if targetLocation doesn't have account info
      if (!accountId) {
        accountId = account?.id;
        if (!accountId) accountId = business?.accountId || business?.accountName;
        if (!accountId && typeof business?.name === 'string' && business.name.includes('accounts/')) {
          const parts = String(business.name).split('/');
          const idx = parts.indexOf('accounts');
          if (idx !== -1 && parts[idx + 1]) accountId = parts[idx + 1];
        }
      }

      // Extract locationId properly - only from GMB resource names, not business names
      let locationId = undefined;
      const locationName = targetLocation?.name || '';

      // Only extract locationId if locationName looks like a GMB resource path
      if (locationName && locationName.includes('accounts/') && locationName.includes('locations/')) {
        locationId = String(locationName).split('/').pop();
      } else if (targetLocation?.locationId) {
        locationId = targetLocation.locationId;
      } else if (business?.locationId) {
        locationId = business.locationId;
      } else if (business?.id && String(business.id).match(/^\d+$/)) {
        // If business.id is numeric, it might be the location ID
        locationId = business.id;
      }

      // Additional fallback: if we have reviewsData with account/location info, use that
      if (!accountId && reviewsData?.reviews?.length > 0) {
        const firstReview = reviewsData.reviews[0];
        if (firstReview?.name && firstReview.name.includes('accounts/')) {
          const parts = String(firstReview.name).split('/');
          const idx = parts.indexOf('accounts');
          if (idx !== -1 && parts[idx + 1]) accountId = parts[idx + 1];

          // Also try to extract locationId from review name
          const locIdx = parts.indexOf('locations');
          if (locIdx !== -1 && parts[locIdx + 1] && !locationId) {
            locationId = parts[locIdx + 1];
          }
        }
      }

      // Fallback discovery: if we have a locationId but still no accountId, try to discover via accounts â†’ locations
      if (!accountId && locationId) {
        try {
          const accountsList = await GMBService.getAccounts();
          if (Array.isArray(accountsList)) {
            for (const acc of accountsList) {
              const accName = acc?.name; // 'accounts/<id>'
              const accIdCandidate = typeof accName === 'string' && accName.includes('accounts/') ? accName.split('/')[1] : undefined;
              if (!accIdCandidate) continue;
              try {
                const locs = await GMBService.getLocations(undefined, accName);
                const match = Array.isArray(locs)
                  ? locs.find(loc => {
                    const locName = String(loc?.name || '');
                    const idFromName = locName && locName.includes('locations/') ? locName.split('/').pop() : '';
                    return idFromName === String(locationId);
                  })
                  : null;
                if (match) {
                  accountId = accIdCandidate;
                  break;
                }
              } catch (locErr) {
                logger.warn('[Photos] Discovery: failed to list locations for', accName, locErr?.message || locErr);
              }
            }
          }
        } catch (discErr) {
          logger.warn('[Photos] Account discovery via locationId failed:', discErr?.message || discErr);
        }
      }


      try {

        if (accountId && locationId) {
          logger.log('[Photos] Fetching media from GMB API:', { accountId, locationId });

          const accessToken = await GMBService.getAccessToken();
          const mediaUrl = `https://mybusiness.googleapis.com/v4/accounts/${accountId}/locations/${locationId}/media`;

          const response = await GMBService._googleFetch(
            mediaUrl,
            { method: 'GET' },
            accessToken
          );

          if (response.ok) {
            const mediaData = await response.json();
            photos = Array.isArray(mediaData.mediaItems) ? mediaData.mediaItems : [];
            totalPhotos = photos.length;

            logger.log('[Photos] Successfully fetched media data:', {
              totalItems: totalPhotos,
              mediaUrl,
              sampleItem: photos[0] || 'none'
            });
          } else {
            const errorData = await response.json().catch(() => ({}));
            logger.warn('[Photos] Failed to fetch media from GMB API:', {
              status: response.status,
              error: errorData,
              mediaUrl
            });
          }
        } else {
          logger.warn('[Photos] Missing accountId or locationId for media API call:', {
            accountId,
            locationId,
            accountCandidates: [account?.id, business?.accountId, business?.accountName],
            locationCandidates: [targetLocation?.name]
          });
        }
      } catch (error) {
        logger.error('[Photos] Error fetching media from GMB API:', error);
      }

      // Analyze photo types
      photos.forEach((photo, index) => {
        const description = (photo.description || '').toLowerCase();
        const mediaFormat = (photo.mediaFormat || '').toLowerCase();
        const categoryType = (photo.categoryType || '').toLowerCase();

        // Check for cover photo (usually the first photo or one with cover/hero keywords)
        if (index === 0 || description.includes('cover') || description.includes('hero') ||
          description.includes('main') || description.includes('banner') ||
          categoryType.includes('cover') || categoryType.includes('profile')) {
          hasCoverPhoto = true;
        }

        // Check for logo photo
        if (description.includes('logo') || description.includes('brand') ||
          description.includes('sign') || description.includes('storefront') ||
          categoryType.includes('logo') || categoryType.includes('exterior')) {
          hasLogoPhoto = true;
        }

        // Check for menu photos (for hotel/restaurant businesses)
        if (description.includes('menu') || description.includes('food') ||
          description.includes('dish') || description.includes('meal') ||
          description.includes('drink') || description.includes('beverage') ||
          categoryType.includes('food') || categoryType.includes('menu')) {
          hasMenuPhotos = true;
        }
      });

      // If no explicit cover photo found but photos exist, assume first photo is cover
      if (!hasCoverPhoto && totalPhotos > 0) {
        hasCoverPhoto = true;
      }

      // Calculate photo score based on business type
      let coverPts = 0;
      let logoPts = 0;
      let menuPts = 0;
      let volumePts = 0;

      if (isHotelRestaurantBusiness) {
        // Hotel/Restaurant scoring: cover(2) + logo(4) + menu(8) + volume(20) = max 34, capped at 30
        coverPts = hasCoverPhoto ? 2 : 0;
        logoPts = hasLogoPhoto ? 4 : 0;
        menuPts = hasMenuPhotos ? 8 : 0;
        volumePts = totalPhotos >= 5 ? 20 : 0;
      } else {
        // Regular business scoring: cover(3) + logo(7) + volume(20) = max 30
        coverPts = hasCoverPhoto ? 3 : 0;
        logoPts = hasLogoPhoto ? 7 : 0;
        volumePts = totalPhotos >= 5 ? 20 : 0;
      }

      photosPts = Math.min(30, coverPts + logoPts + menuPts + volumePts);
      score += photosPts;

      logger.log('[Score] Photos analysis:', {
        businessType: isHotelRestaurantBusiness ? 'Hotel/Restaurant' : 'Regular',
        totalPhotos,
        hasCoverPhoto,
        hasLogoPhoto,
        hasMenuPhotos: isHotelRestaurantBusiness ? hasMenuPhotos : 'N/A',
        apiEndpoint: accountId && locationId ? `https://mybusiness.googleapis.com/v4/accounts/${accountId}/locations/${locationId}/media` : 'N/A',
        scoring: {
          cover: `${hasCoverPhoto ? 'âœ“' : 'âœ—'} ${coverPts}pts`,
          logo: `${hasLogoPhoto ? 'âœ“' : 'âœ—'} ${logoPts}pts`,
          menu: isHotelRestaurantBusiness ? `${hasMenuPhotos ? 'âœ“' : 'âœ—'} ${menuPts}pts` : 'N/A',
          volume: `${totalPhotos >= 5 ? 'âœ“' : 'âœ—'} ${volumePts}pts (${totalPhotos}/5)`
        },
        totalAwarded: photosPts,
        maxPossible: 30
      });

      // 9) Review Rating scoring
      let reviewRatingPts = 0;
      updateProgress('Fetching business rating...', 88, ['Categories & Services']);
      await new Promise(resolve => setTimeout(resolve, 400));

      // Check multiple sources for business rating
      const ratingCandidates = {
        targetLocationRating: targetLocation?.rating,
        targetLocationAverageRating: targetLocation?.averageRating,
        targetLocationGoogleRating: targetLocation?.googleRating,
        businessRating: business?.rating,
        businessAverageRating: business?.averageRating,
        businessGoogleRating: business?.googleRating,
        reviewsDataRating: reviewsData?.averageRating,
        stateLocationRating: state?.locationData?.rating || state?.selectedLocation?.rating,
        stateLocationAverageRating: state?.locationData?.averageRating || state?.selectedLocation?.averageRating
      };

      let myRating = (
        targetLocation?.rating ||
        targetLocation?.averageRating ||
        targetLocation?.googleRating ||
        business?.rating ||
        business?.averageRating ||
        business?.googleRating ||
        reviewsData?.averageRating ||
        state?.locationData?.rating ||
        state?.selectedLocation?.rating ||
        state?.locationData?.averageRating ||
        state?.selectedLocation?.averageRating ||
        0
      );

      // If no rating found in existing data, fetch from Places API
      if (!myRating || myRating === 0) {
        try {
          const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || process.env.REACT_APP_GMB_API_KEY;
          const base = process.env.NODE_ENV === 'development' ? '/places' : 'https://maps.googleapis.com';

          // Get place_id from various sources
          let placeId = (
            targetLocation?.metadata?.placeId ||
            targetLocation?.placeId ||
            business?.placeId ||
            business?.place_id ||
            state?.locationData?.placeId ||
            state?.selectedLocation?.placeId ||
            null
          );

          // If no place_id, try to find it using business name and address
          if (!placeId && apiKey) {
            const businessTitle = targetLocation?.title || business?.title || business?.businessName || '';
            const displayAddress = (freshAddress || resolvedAddress || fallbackBusinessesAddress || '').trim();
            const queryParts = [businessTitle, displayAddress].filter(Boolean).join(' ').trim();

            if (queryParts) {
              logger.log('[Review Rating] Finding place_id for rating lookup:', queryParts);
              const params = new URLSearchParams({
                input: queryParts,
                inputtype: 'textquery',
                fields: 'place_id,name,rating,user_ratings_total',
                key: apiKey
              });
              const findUrl = `${base}/maps/api/place/findplacefromtext/json?${params.toString()}`;
              const findResponse = await fetch(findUrl);

              if (findResponse.ok) {
                const findData = await findResponse.json();
                const candidate = Array.isArray(findData?.candidates) ? findData.candidates[0] : null;
                if (candidate?.place_id) {
                  placeId = candidate.place_id;
                  if (candidate.rating) {
                    myRating = candidate.rating;
                    logger.log('[Review Rating] Found rating from Find Place API:', { placeId, rating: myRating });
                  }
                }
              }
            }
          }

          // If we have place_id but still no rating, get details
          if (placeId && (!myRating || myRating === 0) && apiKey) {
            logger.log('[Review Rating] Fetching rating from Place Details API:', placeId);
            const params = new URLSearchParams({
              place_id: placeId,
              fields: 'place_id,name,rating,user_ratings_total',
              key: apiKey
            });
            const detailsUrl = `${base}/maps/api/place/details/json?${params.toString()}`;
            const detailsResponse = await fetch(detailsUrl);

            if (detailsResponse.ok) {
              const detailsData = await detailsResponse.json();
              if (detailsData?.result?.rating) {
                myRating = detailsData.result.rating;
                logger.log('[Review Rating] Found rating from Place Details API:', {
                  placeId,
                  rating: myRating,
                  totalReviews: detailsData.result.user_ratings_total
                });
              }
            }
          }
        } catch (error) {
          logger.warn('[Review Rating] Failed to fetch rating from Places API:', error);
        }
      }

      logger.log('[Review Rating] Starting calculation:', {
        myRating,
        ratingCandidates,
        fetchedFromAPI: myRating > 0 && !Object.values(ratingCandidates).some(r => r > 0),
        formula: 'MIN((rating - 1) * 2.5, 20)',
        maxPoints: 20
      });

      if (myRating > 1) {
        const rawCalculation = (myRating - 1) * 2.5;
        reviewRatingPts = Math.min(20, rawCalculation);
        score += reviewRatingPts;
        logger.log('[Review Rating] Calculation complete:', {
          myRating,
          rawCalculation: rawCalculation.toFixed(2),
          finalPoints: reviewRatingPts,
          capped: reviewRatingPts === 20,
          formula: `(${myRating} - 1) * 2.5 = ${rawCalculation.toFixed(2)} â†’ ${reviewRatingPts} points`
        });
      } else {
        logger.log('[Review Rating] No points awarded - rating must be > 1.0');
      }

      // 10) Response Rate scoring
      let responseRatePts = 0;
      updateProgress('Fetching response rate data...', 90, ['Review Rating']);
      await new Promise(resolve => setTimeout(resolve, 400));

      // STEP A: Compute from live reviews (90d) â€“ mirrors BusinessProfile logic
      // Derive stable account and location names/ids from multiple sources
      let responseRate = 0;
      try {
        // 1) Try to get full resource names directly
        let accName = undefined;
        let locName = undefined;
        const tlName = targetLocation?.name;
        if (typeof tlName === 'string' && tlName.includes('accounts/') && tlName.includes('locations/')) {
          locName = tlName; // accounts/<id>/locations/<id>
          accName = tlName.split('/').slice(0, 2).join('/'); // accounts/<id>
        }

        // 2) If missing, build from available IDs
        if (!accName) {
          let accId = account?.id || business?.accountId || business?.accountName;
          // allow parsing from account.name if present
          if (!accId && typeof account?.name === 'string' && account.name.includes('accounts/')) {
            const parts = String(account.name).split('/');
            const idx = parts.indexOf('accounts');
            if (idx !== -1 && parts[idx + 1]) accId = parts[idx + 1];
          }
          if (!accId && typeof business?.name === 'string' && business.name.includes('accounts/')) {
            const parts = String(business.name).split('/');
            const idx = parts.indexOf('accounts');
            if (idx !== -1 && parts[idx + 1]) accId = parts[idx + 1];
          }
          // parse from state businesses if needed
          if (!accId && Array.isArray(state?.businesses)) {
            const withAcc = state.businesses.find(b => b?.accountId || b?.accountName || (typeof b?.name === 'string' && b.name.includes('accounts/')));
            if (withAcc) {
              accId = withAcc.accountId || withAcc.accountName;
              if (!accId && typeof withAcc.name === 'string' && withAcc.name.includes('accounts/')) {
                const parts = String(withAcc.name).split('/');
                const idx = parts.indexOf('accounts');
                if (idx !== -1 && parts[idx + 1]) accId = parts[idx + 1];
              }
            }
          }
          if (accId) accName = String(accId).includes('accounts/') ? String(accId) : `accounts/${accId}`;
        }

        let locationId = undefined;
        if (!locName) {
          // Prefer extracting from resource names or explicit IDs
          if (typeof tlName === 'string' && tlName.includes('accounts/') && tlName.includes('locations/')) {
            locationId = tlName.split('/').pop();
          } else if (targetLocation?.locationId) {
            locationId = targetLocation.locationId;
          } else if (targetLocation?.metadata?.locationId) {
            locationId = targetLocation.metadata.locationId;
          } else if (business?.locationId) {
            locationId = business.locationId;
          } else if (business?.id && String(business.id).match(/^\d+$/)) {
            locationId = business.id;
          } else if (typeof business?.name === 'string' && business.name.includes('accounts/') && business.name.includes('locations/')) {
            locationId = String(business.name).split('/').pop();
          } else if (typeof state?.locationData?.name === 'string' && state.locationData.name.includes('locations/')) {
            locationId = String(state.locationData.name).split('/').pop();
          } else if (typeof state?.selectedLocation?.name === 'string' && state.selectedLocation.name.includes('locations/')) {
            locationId = String(state.selectedLocation.name).split('/').pop();
          } else if (state?.locationData?.locationId) {
            locationId = state.locationData.locationId;
          } else if (state?.selectedLocation?.locationId) {
            locationId = state.selectedLocation.locationId;
          }
          if (accName && locationId) {
            locName = `${accName}/locations/${locationId}`;
          }
        }

        try {
          logger.debug('[Response Rate] Derived resource names:', {
            accName,
            locName,
            candidates: {
              account: { id: account?.id, name: account?.name },
              business: { accountId: business?.accountId, accountName: business?.accountName, name: business?.name, locationId: business?.locationId, id: business?.id },
              targetLocation: { name: targetLocation?.name, locationId: targetLocation?.locationId, metaLocationId: targetLocation?.metadata?.locationId },
              stateNames: { locData: state?.locationData?.name, selectedLoc: state?.selectedLocation?.name },
              stateIds: { locDataId: state?.locationData?.locationId, selectedLocId: state?.selectedLocation?.locationId }
            }
          });
        } catch { }

        // 3) Final fallback discovery via accounts -> locations if still missing
        if ((!accName || !locName)) {
          try {
            const accountsListRR = await GMBService.getAccounts();
            let discoveredAccIdRR = undefined;
            let discoveredLocNameRR = undefined;
            const selNameRR = business?.title || business?.businessName || business?.name || '';
            const locIdRR = locationId || targetLocation?.locationId || business?.locationId || (typeof tlName === 'string' && tlName.includes('locations/') ? String(tlName).split('/').pop() : undefined);
            if (Array.isArray(accountsListRR)) {
              for (const acc of accountsListRR) {
                const accResName = acc?.name; // 'accounts/<id>'
                const accIdCandidateRR = typeof accResName === 'string' && accResName.includes('accounts/') ? accResName.split('/')[1] : undefined;
                if (!accIdCandidateRR) continue;
                try {
                  const locsRR = await GMBService.getLocations(undefined, accResName);
                  const byIdRR = (loc) => (loc?.name && (loc.name.includes(String(locIdRR)) || String(loc.name).split('/').pop() === String(locIdRR)));
                  const byTitleRR = (loc) => selNameRR && loc?.title && String(loc.title).toLowerCase() === String(selNameRR).toLowerCase();
                  const matchRR = Array.isArray(locsRR) ? (locsRR.find(byIdRR) || locsRR.find(byTitleRR)) : null;
                  if (matchRR?.name) {
                    discoveredAccIdRR = accIdCandidateRR;
                    discoveredLocNameRR = matchRR.name;
                    break;
                  }
                } catch (e) {
                  logger.warn('[Response Rate] Fallback discovery: failed to list locations for', accResName, e?.message || e);
                }
              }
            }
            if (!accName && discoveredAccIdRR) accName = `accounts/${discoveredAccIdRR}`;
            if (!locName && discoveredLocNameRR) locName = discoveredLocNameRR;
          } catch (e) {
            logger.warn('[Response Rate] Fallback discovery failed:', e?.message || e);
          }
        }

        if (accName && locName) {
          logger.log('[Response Rate] Computing from live reviews (90d)...', { accName, locName });
          const rr = await GMBService.getResponseRateFromReviews(undefined, accName, locName, 90);
          if (rr?.responseRate > 0) {
            responseRate = rr.responseRate;
            logger.log('[Response Rate] Using value from live reviews', {
              responseRate: `${responseRate.toFixed(2)}%`,
              counted: rr.counted,
              replied: rr.replied,
              windowDays: rr.days
            });
          } else {
            logger.log('[Response Rate] Live reviews yielded 0 or no data; will try Performance API next.');
          }
        } else {
          logger.log('[Response Rate] Missing account or location name for reviews-based computation.');
        }
      } catch (error) {
        logger.warn('[Response Rate] Error computing from live reviews:', error);
      }

      // STEP B: Try Performance API as secondary (90 days)
      if (!responseRate || responseRate === 0) {
        try {
          // Reuse and extend derivation for Performance API
          let accountName = undefined;
          let locationIdFromName = undefined;
          const tlName2 = targetLocation?.name;
          if (typeof tlName2 === 'string' && tlName2.includes('accounts/') && tlName2.includes('locations/')) {
            accountName = tlName2.split('/').slice(0, 2).join('/');
            locationIdFromName = tlName2.split('/').pop();
          }
          if (!accountName) {
            let accId2 = account?.id || business?.accountId || business?.accountName;
            if (!accId2 && typeof account?.name === 'string' && account.name.includes('accounts/')) {
              const parts = String(account.name).split('/');
              const idx = parts.indexOf('accounts');
              if (idx !== -1 && parts[idx + 1]) accId2 = parts[idx + 1];
            }
            if (!accId2 && typeof business?.name === 'string' && business.name.includes('accounts/')) {
              const parts = String(business.name).split('/');
              const idx = parts.indexOf('accounts');
              if (idx !== -1 && parts[idx + 1]) accId2 = parts[idx + 1];
            }
            if (!accId2 && Array.isArray(state?.businesses)) {
              const withAcc2 = state.businesses.find(b => b?.accountId || b?.accountName || (typeof b?.name === 'string' && b.name.includes('accounts/')));
              if (withAcc2) {
                accId2 = withAcc2.accountId || withAcc2.accountName;
                if (!accId2 && typeof withAcc2.name === 'string' && withAcc2.name.includes('accounts/')) {
                  const parts = String(withAcc2.name).split('/');
                  const idx = parts.indexOf('accounts');
                  if (idx !== -1 && parts[idx + 1]) accId2 = parts[idx + 1];
                }
              }
            }
            if (accId2) accountName = String(accId2).includes('accounts/') ? String(accId2) : `accounts/${accId2}`;
          }
          if (!locationIdFromName) {
            let locId2 = undefined;
            if (typeof tlName2 === 'string' && tlName2.includes('locations/')) locId2 = String(tlName2).split('/').pop();
            if (!locId2 && targetLocation?.locationId) locId2 = targetLocation.locationId;
            if (!locId2 && targetLocation?.metadata?.locationId) locId2 = targetLocation.metadata.locationId;
            if (!locId2 && business?.locationId) locId2 = business.locationId;
            if (!locId2 && business?.id && String(business.id).match(/^\d+$/)) locId2 = business.id;
            if (!locId2 && typeof business?.name === 'string' && business.name.includes('locations/')) locId2 = String(business.name).split('/').pop();
            if (!locId2 && typeof state?.locationData?.name === 'string' && state.locationData.name.includes('locations/')) locId2 = String(state.locationData.name).split('/').pop();
            if (!locId2 && typeof state?.selectedLocation?.name === 'string' && state.selectedLocation.name.includes('locations/')) locId2 = String(state.selectedLocation.name).split('/').pop();
            if (!locId2 && state?.locationData?.locationId) locId2 = state.locationData.locationId;
            if (!locId2 && state?.selectedLocation?.locationId) locId2 = state.selectedLocation.locationId;
            if (accountName && locId2) locationIdFromName = String(locId2);
          }

          try {
            logger.debug('[Response Rate] Performance API derived:', { accountName, locationIdFromName });
          } catch { }

          // Final fallback discovery for Performance API if still missing pieces
          if (!accountName || !locationIdFromName) {
            try {
              const accountsListPerf = await GMBService.getAccounts();
              const selNamePerf = business?.title || business?.businessName || business?.name || '';
              let discoveredAccIdPerf = undefined;
              let discoveredLocIdPerf = undefined;
              if (Array.isArray(accountsListPerf)) {
                for (const acc of accountsListPerf) {
                  const accResName = acc?.name; // 'accounts/<id>'
                  const accIdCandidatePerf = typeof accResName === 'string' && accResName.includes('accounts/') ? accResName.split('/')[1] : undefined;
                  if (!accIdCandidatePerf) continue;
                  try {
                    const locsPerf = await GMBService.getLocations(undefined, accResName);
                    const byTitlePerf = (loc) => selNamePerf && loc?.title && String(loc.title).toLowerCase() === String(selNamePerf).toLowerCase();
                    const matchPerf = Array.isArray(locsPerf) ? (locsPerf.find(byTitlePerf)) : null;
                    if (matchPerf?.name?.includes('locations/')) {
                      discoveredAccIdPerf = accIdCandidatePerf;
                      discoveredLocIdPerf = String(matchPerf.name).split('/').pop();
                      break;
                    }
                  } catch (e) {
                    logger.warn('[Response Rate] Performance fallback: failed to list locations for', accResName, e?.message || e);
                  }
                }
              }
              if (!accountName && discoveredAccIdPerf) accountName = `accounts/${discoveredAccIdPerf}`;
              if (!locationIdFromName && discoveredLocIdPerf) locationIdFromName = discoveredLocIdPerf;
            } catch (e) {
              logger.warn('[Response Rate] Performance fallback discovery failed:', e?.message || e);
            }
          }

          if (accountName && locationIdFromName) {
            logger.log('[Response Rate] Fetching via Performance API (90d)...');
            const now = new Date();
            const start = new Date(now);
            start.setDate(now.getDate() - 90);
            const dateRange = {
              startDate: { year: start.getFullYear(), month: start.getMonth() + 1, day: start.getDate() },
              endDate: { year: now.getFullYear(), month: now.getMonth() + 1, day: now.getDate() }
            };
            const responseMetrics = await GMBService.getResponseRateMetrics(undefined, locationIdFromName, dateRange);
            if (responseMetrics?.responseRate && responseMetrics.responseRate > 0) {
              responseRate = responseMetrics.responseRate;
              logger.log('[Response Rate] Using value from Performance API', { responseRate: `${responseRate}%`, dateRange: '90d' });
            } else {
              logger.log('[Response Rate] Performance API returned no response rate.');
            }
          } else {
            logger.log('[Response Rate] Missing account/location IDs for Performance API fetch.');
          }
        } catch (error) {
          logger.warn('[Response Rate] Failed to fetch from Performance API:', error);
        }
      }

      // STEP C: Last-resort internal candidates only (no Your Data fallback by design)
      if (!responseRate || responseRate === 0) {
        const responseRateCandidates = {
          performanceDataResponseRate: performanceData?.responseRate,
          performanceDataAvgResponseRate: performanceData?.averageResponseRate,
          performanceDataMessagingResponseRate: performanceData?.messagingResponseRate,
          targetLocationResponseRate: targetLocation?.responseRate,
          businessResponseRate: business?.responseRate
        };

        responseRate = (
          (performanceData?.responseRate ?? performanceData?.averageResponseRate ?? performanceData?.messagingResponseRate) ??
          targetLocation?.responseRate ??
          business?.responseRate ??
          0
        );

        logger.log('[Response Rate] Using in-memory candidates as last resort', { responseRate, responseRateCandidates });
      }

      logger.log('[Response Rate] Starting calculation:', {
        responseRate,
        formula: '(Response Rate) Ã— (8 / 100)',
        maxPoints: 8
      });

      if (responseRate > 0) {
        // Formula: (Response Rate) Ã— (8 / 100)
        // Response rate is typically a percentage (0-100), so we use it directly
        responseRatePts = (responseRate / 100) * 8;
        score += responseRatePts;
        logger.log('[Response Rate] Calculation complete:', {
          responseRate: `${responseRate}%`,
          calculation: `(${responseRate} / 100) Ã— 8 = ${responseRatePts.toFixed(2)}`,
          finalPoints: responseRatePts,
          formula: `${responseRate}% response rate â†’ ${responseRatePts.toFixed(2)} points`
        });
      } else {
        logger.log('[Response Rate] No points awarded - no response rate data found');
      }

      // 11) Reviews vs competitors (Places Nearby)
      let reviewsPts = 0;
      // Expose myTotal and compAvg to the whole computeScore closure for later use in breakdown
      let myTotal = 0;
      let compAvg = 0;

      // Declare velocity variables in proper scope
      let velocityScore = 0;
      let velocityLevel = 'N/A';
      let reviewsLast30Days = 0;
      let velocityRecommendations = [];

      // Declare GMB Feed variables in proper scope (used later in breakdown)
      let gmbFeedScore = 0;
      let gmbFeedLevel = 'N/A';
      let gmbFeedRecommendations = [];
      let postsLast90Days = 0;

      try {
        const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || process.env.REACT_APP_GMB_API_KEY;
        // Prefer explicit lat/lng passed from BusinessProfile; then fall back to GMB latlng shapes
        const explicitLatLng = state?.explicitLatLng; // { lat, lng }
        const coordCandidates = {
          explicit: explicitLatLng,
          target: targetLocation?.latlng,
          business: business?.latlng || business?.location?.latlng,
          state: state?.locationData?.latlng || state?.selectedLocation?.latlng
        };
        const fallbackLat = (coordCandidates.explicit?.lat != null)
          ? coordCandidates.explicit.lat
          : (coordCandidates.target?.latitude ?? coordCandidates.business?.latitude ?? coordCandidates.state?.latitude);
        const fallbackLng = (coordCandidates.explicit?.lng != null)
          ? coordCandidates.explicit.lng
          : (coordCandidates.target?.longitude ?? coordCandidates.business?.longitude ?? coordCandidates.state?.longitude);
        // Require explicit user keyword; no fallback to business title
        const keyword = (Array.isArray(keywords) && keywords.length > 0) ? keywords[0] : '';

        const base = process.env.NODE_ENV === 'development' ? '/places' : 'https://maps.googleapis.com';
        const sleep = (ms) => new Promise((res) => setTimeout(res, ms));

        // 9.a Prefer provided place_id from selected business/location; else resolve via Find Place
        let myPlaceId = (
          targetLocation?.metadata?.placeId ||
          targetLocation?.placeId ||
          business?.placeId ||
          business?.place_id ||
          state?.locationData?.placeId ||
          state?.selectedLocation?.placeId ||
          null
        );
        let lat = fallbackLat;
        let lng = fallbackLng;
        const findPlace = async (query) => {
          const params = new URLSearchParams({
            input: query,
            inputtype: 'textquery',
            fields: 'place_id,name,geometry',
            key: apiKey || ''
          });
          const url = `${base}/maps/api/place/findplacefromtext/json?${params.toString()}`;
          const r = await fetch(url);
          if (!r.ok) throw new Error(`FindPlace HTTP ${r.status}`);
          const data = await r.json();
          if (data?.status && data.status !== 'OK' && data.status !== 'ZERO_RESULTS') {
            logger.warn('[Find Place] API status:', data.status, data?.error_message || '');
          }
          return data;
        };

        const getDetails = async (placeId) => {
          const params = new URLSearchParams({
            place_id: placeId,
            fields: 'place_id,name,geometry,formatted_address',
            key: apiKey || ''
          });
          const url = `${base}/maps/api/place/details/json?${params.toString()}`;
          const r = await fetch(url);
          if (!r.ok) throw new Error(`PlaceDetails HTTP ${r.status}`);
          const data = await r.json();
          if (data?.status && data.status !== 'OK') {
            logger.warn('[Place Details] API status:', data.status, data?.error_message || '');
          }
          return data;
        };

        // 9.b Prefer coordinates from Place Details (place_id) for highest precision
        let usedSource = 'fallbacks';
        let coordsResolved = false;
        let geocodeTried = false;
        let geocodeSucceeded = false;
        const hasExplicit = (explicitLatLng && typeof explicitLatLng.lat === 'number' && typeof explicitLatLng.lng === 'number');

        if (myPlaceId) {
          try {
            const det = await getDetails(myPlaceId);
            const loc = det?.result?.geometry?.location;
            if (loc && typeof loc.lat === 'number' && typeof loc.lng === 'number') {
              lat = loc.lat;
              lng = loc.lng;
              usedSource = 'place_details_primary';
              coordsResolved = true;
              logger.debug('[Place Details] Primary coordinates from place_id:', { myPlaceId, lat, lng });
            }
          } catch (e) {
            logger.warn('[Place Details] Primary fetch failed; will try fallbacks. Error:', e?.message || e);
          }
        }

        // If Place Details not available or failed, try explicit GMB coords
        if (!coordsResolved && hasExplicit) {
          lat = explicitLatLng.lat;
          lng = explicitLatLng.lng;
          usedSource = 'explicitLatLng';
          coordsResolved = true;
          logger.debug('[Coords] Using explicit GMB coordinates from BusinessProfile:', { lat, lng });
        }

        // Only geocode address if explicit GMB coords are not provided
        const cityName = city || state?.selectedLocation?.address?.locality || '';
        const buildGeocodeAddress = () => {
          const parts = [];
          const addr = (displayAddress || '').replace(/[\n\r]+/g, ', ');
          if (addr) parts.push(addr);
          if (cityName && !addr.toLowerCase().includes(cityName.toLowerCase())) parts.push(cityName);
          if (businessTitle && !addr.toLowerCase().includes(businessTitle.toLowerCase())) parts.unshift(businessTitle);
          // Normalize commas and spaces
          return parts
            .filter(Boolean)
            .map(s => s.trim())
            .filter(s => s.length > 0)
            .join(', ')
            .replace(/\s*,\s*/g, ', ')
            .replace(/,{2,}/g, ',')
            .replace(/\s+/g, ' ')
            .trim();
        };
        const addressToGeocode = buildGeocodeAddress();
        if (!coordsResolved && apiKey && addressToGeocode) {
          try {
            geocodeTried = true;
            logger.debug('[Geocode] Formatted address:', addressToGeocode);
            const params = new URLSearchParams({ address: addressToGeocode, key: apiKey || '' });
            const url = `${base}/maps/api/geocode/json?${params.toString()}`;
            const r = await fetch(url);
            if (!r.ok) throw new Error(`Geocode HTTP ${r.status}`);
            const data = await r.json();
            if (data?.status === 'OK' && Array.isArray(data.results) && data.results[0]?.geometry?.location) {
              const loc = data.results[0].geometry.location;
              if (typeof loc.lat === 'number' && typeof loc.lng === 'number') {
                lat = loc.lat;
                lng = loc.lng;
                geocodeSucceeded = true;
                usedSource = 'geocode_address';
                coordsResolved = true;
                logger.debug('[Geocode] Using coordinates from address:', { addressToGeocode, lat, lng });
              }
            } else {
              logger.debug('[Geocode] No results or non-OK status:', data?.status, data?.error_message || '');
            }
          } catch (e) {
            logger.warn('[Geocode] Failed to geocode address; will try other sources. Error:', e?.message || e);
          }
        }

        // If still unresolved, prefer Place Details when place_id is known
        if (!geocodeSucceeded && !coordsResolved && myPlaceId) {
          try {
            const det = await getDetails(myPlaceId);
            const loc = det?.result?.geometry?.location;
            if (loc && typeof loc.lat === 'number' && typeof loc.lng === 'number') {
              lat = loc.lat;
              lng = loc.lng;
              usedSource = 'place_details';
              coordsResolved = true;
              logger.debug('[Place Details] Using provided place_id for geometry:', { myPlaceId, lat, lng });
            }
          } catch (e) {
            logger.warn('[Place Details] Failed for provided place_id; falling back to Find Place if possible. Error:', e?.message || e);
          }
        }

        if (!coordsResolved && !myPlaceId) {
          const queryParts = [businessTitle, displayAddress].filter(Boolean).join(' ').trim();
          logger.debug('[Find Place] Query:', queryParts);
          try {
            if (apiKey && queryParts) {
              const fp = await findPlace(queryParts);
              const candidate = Array.isArray(fp?.candidates) ? fp.candidates[0] : null;
              if (candidate?.place_id) {
                myPlaceId = candidate.place_id;
                // Prefer precise geometry from Details (can include more accurate coordinates)
                const det = await getDetails(myPlaceId);
                const loc = det?.result?.geometry?.location || candidate?.geometry?.location;
                if (loc && typeof loc.lat === 'number' && typeof loc.lng === 'number') {
                  lat = loc.lat;
                  lng = loc.lng;
                }
                usedSource = 'find_place+details';
                coordsResolved = true;
                logger.debug('[Find Place] Resolved my place_id and coordinates:', { myPlaceId, lat, lng });
              } else {
                logger.debug('[Find Place] No candidate found. Using fallback coordinates.');
              }
            }
          } catch (e) {
            logger.warn('[Find Place] Failed to resolve place_id; using fallbacks. Error:', e?.message || e);
          }
        }

        if (!coordsResolved) {
          // We are falling back to raw GMB latlng shapes
          usedSource = coordCandidates.target ? 'gmb_target_latlng' : (coordCandidates.business ? 'business_latlng' : (coordCandidates.state ? 'state_latlng' : 'none'));
        }
        logger.debug('[Places Nearby] Coordinate sources:', {
          target: coordCandidates.target,
          business: coordCandidates.business,
          state: coordCandidates.state,
          explicit: coordCandidates.explicit,
          used: { lat, lng },
          usedSource,
          myPlaceId
        });

        const fetchNearby = async (radius, pageToken) => {
          const params = new URLSearchParams({
            location: `${lat},${lng}`,
            radius: String(radius),
            keyword: keyword || '',
            key: apiKey || ''
          });
          if (pageToken) params.append('pagetoken', pageToken);
          const url = `${base}/maps/api/place/nearbysearch/json?${params.toString()}`;
          const r = await fetch(url);
          if (!r.ok) {
            logger.warn('[Places Nearby] HTTP error', r.status, r.statusText);
            throw new Error('Places Nearby fetch failed');
          }
          const data = await r.json();
          if (data?.status && data.status !== 'OK' && data.status !== 'ZERO_RESULTS') {
            logger.warn('[Places Nearby] API status:', data.status, data?.error_message || '');
          }
          return data;
        };

        // If no keyword provided, prompt user and stop
        if (!keyword) {
          logger.debug('[Keywords] No keyword selected. Opening keyword editor dialog...');
          setEditKwOpen(true);
          return;
        }

        const resultsAll = [];
        if (apiKey && lat && lng && keyword) {
          const radii = state?.searchRadiusMeters
            ? [Number(state.searchRadiusMeters)]
            : [1000, 2000, 3000, 5000, 10000, 15000, 20000, 25000];
          console.groupCollapsed('[Places Nearby] Competitor discovery');
          logger.log('Start params:', { lat, lng, keyword, radii });
          const seen = new Set();
          const makeKey = (p) => {
            if (!p) return null;
            if (p.place_id) return `id:${p.place_id}`;
            const n = (p.name || '').toLowerCase().trim();
            const v = (p.vicinity || '').toLowerCase().trim();
            const la = p.geometry?.location?.lat;
            const ln = p.geometry?.location?.lng;
            return `k:${n}|${v}|${la ?? ''},${ln ?? ''}`;
          };
          for (const rad of radii) {
            let pageToken = undefined;
            let pagesFetched = 0;
            logger.log(`Radius ${rad}m: fetching...`);
            do {
              const data = await fetchNearby(rad, pageToken);
              const got = Array.isArray(data?.results) ? data.results.length : 0;
              let dupCount = 0;
              if (Array.isArray(data?.results)) {
                data.results.forEach((p) => {
                  const key = makeKey(p);
                  if (!key) return;
                  if (seen.has(key)) {
                    dupCount += 1; // skip duplicate
                    return;
                  }
                  seen.add(key);
                  resultsAll.push(p);
                });
              }
              pageToken = data?.next_page_token;
              pagesFetched += 1;
              logger.log(`  Page ${pagesFetched}: status=${data?.status || 'n/a'}, got ${got} results, deduped ${dupCount}, total unique so far ${resultsAll.length}, next_page_token=${!!pageToken}`);
              if (pageToken) await sleep(2000);
            } while (pageToken && pagesFetched < 3 && resultsAll.length < 50);

            if (resultsAll.length >= 50) {
              logger.log(`Reached 50+ competitors at radius ${rad}m, stopping radius expansion.`);
              break;
            }
          }

          // Identify "my" place by place_id when available; fallback to name+proximity
          const myName = (businessTitle || '').toLowerCase().trim();
          const toRad = (d) => (d * Math.PI) / 180;
          const haversine = (a, b) => {
            const R = 6371000; // meters
            const dLat = toRad((b.lat || 0) - (a.lat || 0));
            const dLng = toRad((b.lng || 0) - (a.lng || 0));
            const s1 = Math.sin(dLat / 2) ** 2;
            const s2 = Math.cos(toRad(a.lat || 0)) * Math.cos(toRad(b.lat || 0)) * Math.sin(dLng / 2) ** 2;
            return 2 * R * Math.asin(Math.sqrt(s1 + s2));
          };

          myTotal = 0;
          let myCandidate = null;
          let myMatchBy = myPlaceId ? 'place_id' : 'name+proximity';
          if (myPlaceId) {
            myCandidate = resultsAll.find((p) => p.place_id === myPlaceId) || null;
          }
          if (!myCandidate) {
            myCandidate = resultsAll.find((p) => {
              const pName = (p.name || '').toLowerCase().trim();
              if (!p.geometry?.location) return false;
              const dist = haversine({ lat, lng }, { lat: p.geometry.location.lat, lng: p.geometry.location.lng });
              return myName && pName === myName && dist < 100; // name match and within 100m
            }) || null;
          }
          // If still not found and we have a place_id, use Place Details directly as a fallback for "my place"
          if (!myCandidate && myPlaceId) {
            try {
              const det = await getDetails(myPlaceId);
              const r = det?.result;
              if (r) {
                myCandidate = {
                  place_id: r.place_id || myPlaceId,
                  name: r.name,
                  user_ratings_total: r.user_ratings_total ?? 0,
                  rating: r.rating,
                  geometry: r.geometry,
                };
                myMatchBy = 'place_details_fallback';
                logger.debug('[My Place] Using Place Details fallback:', { place_id: myPlaceId, name: r.name, user_ratings_total: myCandidate.user_ratings_total });
              }
            } catch (e) {
              logger.warn('[My Place] Place Details fallback failed:', e?.message || e);
            }
          }
          if (myCandidate?.user_ratings_total != null) myTotal = myCandidate.user_ratings_total;
          logger.log('My place match:', { found: !!myCandidate, myTotal, by: myMatchBy });
          logger.log('Debug - myCandidate data:', myCandidate);
          logger.log('Debug - targetLocation data:', targetLocation);
          logger.log('Debug - business data:', business);
          logger.log('Debug - myPlaceId:', myPlaceId);
          logger.log('Debug - myName:', myName);

          // If not found by exact match, try best proximity by name contains
          if (!myTotal && myName) {
            const prox = resultsAll
              .map((p) => ({
                p,
                dist: p.geometry?.location ? haversine({ lat, lng }, { lat: p.geometry.location.lat, lng: p.geometry.location.lng }) : Infinity,
              }))
              .filter((x) => (x.p.name || '').toLowerCase().includes(myName))
              .sort((a, b) => a.dist - b.dist);
            if (prox[0]?.p?.user_ratings_total != null && prox[0].dist < 200) {
              myTotal = prox[0].p.user_ratings_total;
              myCandidate = prox[0].p; // Update myCandidate to the found match
            }
            logger.log('My place fuzzy/proximity check:', { candidate: prox[0]?.p?.name, dist: prox[0]?.dist, myTotal });
          }

          // Final fallback: use any business with similar name if still no match
          if (!myTotal && myName) {
            const fallbackMatch = resultsAll.find(p =>
              (p.name || '').toLowerCase().includes(myName.toLowerCase()) ||
              myName.toLowerCase().includes((p.name || '').toLowerCase())
            );
            if (fallbackMatch?.user_ratings_total) {
              myTotal = fallbackMatch.user_ratings_total;
              myCandidate = fallbackMatch;
              logger.log('Using fallback business match:', { name: fallbackMatch.name, reviews: myTotal });
            }
          }

          // Competitors average (exclude my candidate if matched)
          const competitors = resultsAll.filter((p) => p !== myCandidate && (p.user_ratings_total ?? 0) >= 0);
          const compTotals = competitors.map((p) => p.user_ratings_total || 0).filter((n) => typeof n === 'number');
          compAvg = compTotals.length ? (compTotals.reduce((s, n) => s + n, 0) / compTotals.length) : 0;
          logger.log('Competitor stats:', { totalFetched: resultsAll.length, competitorCount: competitors.length, compAvg });

          // Calculate velocity score using API data directly instead of competitor search
          // Get actual business data from API calls (not competitor discovery)
          const actualRating = business?.rating || account?.rating || targetLocation?.rating || 0;
          const actualReviewsCount = business?.userRatingCount || business?.reviewsCount || account?.reviewsCount || 0;

          const userBusinessData = {
            name: business?.name || business?.businessName || account?.name || resolvedAccountName,
            rating: actualRating,
            reviewsCount: actualReviewsCount
          };

          logger.log('[Velocity] Using API data directly:', {
            businessData: business,
            accountData: account,
            extractedData: userBusinessData
          });

          // LIVE Velocity fetch from Google My Business API (last 30 days reviews) â€” no CSV/localStorage fallback
          try {
            // Reuse robust ID extraction like GMB Feed block
            const extractFromName = (nameStr) => {
              if (typeof nameStr !== 'string') return { accountId: undefined, locationId: undefined };
              const parts = nameStr.split('/');
              const aIdx = parts.indexOf('accounts');
              const lIdx = parts.indexOf('locations');
              return {
                accountId: aIdx !== -1 && parts[aIdx + 1] ? parts[aIdx + 1] : undefined,
                locationId: lIdx !== -1 && parts[lIdx + 1] ? parts[lIdx + 1] : undefined,
              };
            };

            const accountIdCandidatesV = [];
            const locationIdCandidatesV = [];

            if (account?.id) accountIdCandidatesV.push(String(account.id));
            if (business?.accountId) accountIdCandidatesV.push(String(business.accountId));
            if (targetLocation?.metadata?.locationId) locationIdCandidatesV.push(String(targetLocation.metadata.locationId));
            if (targetLocation?.locationId) locationIdCandidatesV.push(String(targetLocation.locationId));

            [account?.name, business?.name, business?.accountName, targetLocation?.name, state?.locationData?.name, state?.selectedLocation?.name]
              .filter(Boolean)
              .forEach((nm) => {
                const { accountId: a, locationId: l } = extractFromName(String(nm));
                if (a) accountIdCandidatesV.push(String(a));
                if (l) locationIdCandidatesV.push(String(l));
              });

            let accountIdV = accountIdCandidatesV.find(Boolean);
            let locationIdV = locationIdCandidatesV.find(Boolean);

            // Attempt API resolution if missing
            if (!accountIdV || !locationIdV) {
              try {
                logger.warn('[Velocity] Resolving missing IDs via GMBService...');
                const accessToken = await GMBService.getAccessToken();
                const accounts = await GMBService.getAccounts(accessToken);
                const firstAccount = Array.isArray(accounts) && accounts.length ? accounts[0] : null;
                if (!accountIdV && firstAccount?.name?.includes('accounts/')) {
                  accountIdV = String(firstAccount.name).split('/')[1];
                }
                if (accountIdV) {
                  const accountNameFull = `accounts/${accountIdV}`;
                  const locs = await GMBService.getLocations(accessToken, accountNameFull);
                  const myTitle = targetLocation?.title || business?.title || business?.businessName || '';
                  const myPlaceId = targetLocation?.metadata?.placeId || targetLocation?.placeId || business?.placeId || business?.place_id || null;
                  const myLat = targetLocation?.latlng?.latitude ?? business?.latlng?.latitude ?? null;
                  const myLng = targetLocation?.latlng?.longitude ?? business?.latlng?.longitude ?? null;
                  const byPlaceId = (loc) => myPlaceId && (loc?.metadata?.placeId === myPlaceId);
                  const byTitle = (loc) => myTitle && (loc?.title && String(loc.title).toLowerCase() === String(myTitle).toLowerCase());
                  const byProximity = (loc) => {
                    try {
                      const ll = loc?.latlng; if (!ll || myLat == null || myLng == null) return false;
                      const dLat = Math.abs((ll.latitude ?? ll.lat) - myLat);
                      const dLng = Math.abs((ll.longitude ?? ll.lng) - myLng);
                      return (dLat + dLng) < 0.02;
                    } catch { return false; }
                  };
                  const match = Array.isArray(locs) ? (locs.find(byPlaceId) || locs.find(byTitle) || locs.find(byProximity)) : null;
                  if (match?.name?.includes('locations/')) {
                    locationIdV = String(match.name).split('/').pop();
                    logger.debug('[Velocity] Resolved locationId via API:', locationIdV);
                  }
                }
              } catch (e) {
                logger.warn('[Velocity] Failed to resolve IDs via GMBService:', e);
              }
            }

            if (!accountIdV || !locationIdV) {
              logger.warn('[Velocity] Missing accountId or locationId; skipping live velocity fetch', { accountIdV, locationIdV });
            } else {
              const token = await GMBService.getAccessToken();
              const { reviewsCount } = await VelocityService.fetchLast30DaysReviews(accountIdV, locationIdV, token);
              const benchmarkReviewCount = 20;
              const vResult = VelocityService.calculateVelocityScore(reviewsCount, benchmarkReviewCount);
              velocityScore = vResult.velocityScore;
              velocityLevel = vResult.level;
              reviewsLast30Days = vResult.reviewsLast30Days;
              velocityRecommendations = vResult.recommendations;

              // Save profile strength data for task generation
              const profileData = {
                business: business,
                profileScore: velocityScore || 0,
                gaps: {
                  website: !business.websiteUri,
                  photos: (business.photos?.length || 0) < 10,
                  hours: !business.regularHours?.periods?.length,
                  attributes: !business.attributes?.length,
                  description: !business.profile?.description
                },
                competitors: competitors || []
              };

              ProfileStrengthTaskService.saveProfileStrengthData(profileData);

              logger.log('=== VELOCITY SCORING RESULTS (LIVE) ===');
              logger.log('Reviews Last 30 Days:', reviewsLast30Days);
              logger.log('Velocity Score:', velocityScore);
              logger.log('Velocity Level:', velocityLevel);
              logger.log('Performance Percentage:', Math.round((velocityScore / 12) * 100) + '%');
              logger.log('Velocity Recommendations:', velocityRecommendations);
              logger.log('================================');
            }
          } catch (err) {
            logger.warn('[Velocity] Live velocity fetch failed:', err);
          }

          // Calculate GMB Feed Score (assign to outer-scoped vars) - API only, no CSV/local fallbacks
          try {
            // Robustly derive accountId and locationId from multiple shapes
            const extractFromName = (nameStr) => {
              if (typeof nameStr !== 'string') return { accountId: undefined, locationId: undefined };
              const parts = nameStr.split('/');
              const aIdx = parts.indexOf('accounts');
              const lIdx = parts.indexOf('locations');
              return {
                accountId: aIdx !== -1 && parts[aIdx + 1] ? parts[aIdx + 1] : undefined,
                locationId: lIdx !== -1 && parts[lIdx + 1] ? parts[lIdx + 1] : undefined,
              };
            };

            const accountIdCandidates = [];
            const locationIdCandidates = [];

            // Direct IDs
            if (account?.id) accountIdCandidates.push(String(account.id));
            if (business?.accountId) accountIdCandidates.push(String(business.accountId));
            if (targetLocation?.metadata?.locationId) locationIdCandidates.push(String(targetLocation.metadata.locationId));
            if (targetLocation?.locationId) locationIdCandidates.push(String(targetLocation.locationId));

            // From names that may contain both accounts/{id}/locations/{id}
            [account?.name, business?.name, business?.accountName, targetLocation?.name, state?.locationData?.name, state?.selectedLocation?.name]
              .filter(Boolean)
              .forEach((nm) => {
                const { accountId: a, locationId: l } = extractFromName(String(nm));
                if (a) accountIdCandidates.push(String(a));
                if (l) locationIdCandidates.push(String(l));
              });

            // Final chosen values
            let accountId = accountIdCandidates.find(Boolean);
            let locationIdFromName = locationIdCandidates.find(Boolean);

            // If either is missing, try to resolve by fetching accounts/locations
            if (!accountId || !locationIdFromName) {
              try {
                logger.warn('[GMBFeed] Attempting to resolve missing IDs via GMBService...', {
                  haveAccountId: !!accountId, haveLocationId: !!locationIdFromName
                });
                const accessToken = await GMBService.getAccessToken();
                const accounts = await GMBService.getAccounts(accessToken);
                // Prefer explicit account match if any candidate equals fetched account id
                const firstAccount = Array.isArray(accounts) && accounts.length > 0 ? accounts[0] : null;
                if (!accountId && firstAccount?.name?.includes('accounts/')) {
                  accountId = String(firstAccount.name).split('/')[1];
                }

                if (accountId) {
                  const accountNameFull = `accounts/${accountId}`;
                  const locs = await GMBService.getLocations(accessToken, accountNameFull);
                  const myTitle = targetLocation?.title || business?.title || business?.businessName || '';
                  const myPlaceId = targetLocation?.metadata?.placeId || targetLocation?.placeId || business?.placeId || business?.place_id || null;
                  const myLat = targetLocation?.latlng?.latitude ?? business?.latlng?.latitude ?? null;
                  const myLng = targetLocation?.latlng?.longitude ?? business?.latlng?.longitude ?? null;

                  const byPlaceId = (loc) => myPlaceId && (loc?.metadata?.placeId === myPlaceId);
                  const byTitle = (loc) => myTitle && (loc?.title && String(loc.title).toLowerCase() === String(myTitle).toLowerCase());
                  const byProximity = (loc) => {
                    try {
                      const ll = loc?.latlng;
                      if (!ll || myLat == null || myLng == null) return false;
                      const dLat = Math.abs((ll.latitude ?? ll.lat) - myLat);
                      const dLng = Math.abs((ll.longitude ?? ll.lng) - myLng);
                      return (dLat + dLng) < 0.02; // ~ within a couple km
                    } catch { return false; }
                  };
                  const match = Array.isArray(locs) ? (locs.find(byPlaceId) || locs.find(byTitle) || locs.find(byProximity)) : null;
                  if (match?.name?.includes('locations/')) {
                    locationIdFromName = String(match.name).split('/').pop();
                    logger.debug('[GMBFeed] Resolved locationId via API:', locationIdFromName);
                  }
                }
              } catch (resolveErr) {
                logger.warn('[GMBFeed] Failed to resolve IDs via GMBService:', resolveErr);
              }
            }

            if (!accountId || !locationIdFromName) {
              logger.warn('[GMBFeed] Missing accountId or locationId; skipping GMB Feed scoring', {
                accountIdCandidates,
                locationIdCandidates,
                targetLocationName: targetLocation?.name,
                businessName: business?.name,
                accountName: account?.name
              });
            } else {
              const accessToken = await GMBService.getAccessToken();
              const { postsCount } = await GMBFeedService.fetchLast90DaysPosts(accountId, locationIdFromName, accessToken);

              postsLast90Days = postsCount || 0;
              const gmbFeedResult = GMBFeedService.calculateGMBFeedScore(postsLast90Days, 8);

              gmbFeedScore = gmbFeedResult.feedScore;
              gmbFeedLevel = gmbFeedResult.level;
              gmbFeedRecommendations = gmbFeedResult.recommendations;

              logger.log('=== GMB FEED SCORING RESULTS ===');
              logger.log('GMB Feed Score:', gmbFeedScore);
              logger.log('GMB Feed Level:', gmbFeedLevel);
              logger.log('Posts Last 90 Days:', postsLast90Days);
              logger.log('Feed Recommendations:', gmbFeedRecommendations);
              logger.log('Raw Calculation: MIN((' + postsLast90Days + ' / 8) * 30, 30) = ' + gmbFeedScore);
              logger.log('================================');
            }
          } catch (error) {
            logger.error('[GMBFeed] Error calculating GMB Feed score:', error);
          }

          // Removed competitor products scraping and product-based comparison bonus
          // Proceed with generic competitor analysis without product data
          updateProgress('Analyzing competitors...', 96, ['Competitor Analysis']);
          await new Promise(resolve => setTimeout(resolve, 500));
          // Retain competitor discovery and distance table without product counts
          try {
            const compRows = competitors.map((p) => {
              const plat = p.geometry?.location?.lat;
              const plng = p.geometry?.location?.lng;
              const dist = (plat != null && plng != null) ? Math.round(haversine({ lat, lng }, { lat: plat, lng: plng })) : null;
              return {
                name: p.name || '(no name)',
                rating: p.rating ?? null,
                user_ratings_total: p.user_ratings_total ?? 0,
                distance_m: dist
              };
            });
            if (compRows.length) {
              console.table(compRows);
            }
          } catch (_) {
            // ignore console.table errors in some environments
          }

          if (compAvg > 0 && myTotal >= 0) {
            reviewsPts = Math.min(30, Math.round((myTotal / compAvg) * 30));
            score += reviewsPts;
            logger.log('Review-based points computed:', { myTotal, compAvg, reviewsPts, capped: reviewsPts === 30 });
          }
          console.groupEnd();
        }
      } catch (e) {
        // Swallow errors; keep points at 0
      }

      // Console breakdown (raw only)
      try {
        console.groupCollapsed('[Profile Strength] Scoring Breakdown');
        logger.log('Account verificationState:', account?.verificationState);
        // Note: Velocity and GMB Feed variables are now declared in proper scope above

        const score = verPts + namePts + addrPts + phonePts + descPts + websitePts + hoursPts + labelPts + catBasePts + catExtraPts + catCityPts + socialPts + appointmentsPts + serviceAreaPts + bookApptPts + qaPts + photosPts + reviewRatingPts + responseRatePts + reviewsPts;

        logger.log('Target location:', targetLocation?.name || targetLocation?.title || '(none)');
        logger.log('Title:', businessTitle);

        console.groupEnd();
      } catch (_) { }

      if (isMounted) {
        // Compute final total and persist snapshot for dashboard
        const totalScoreAll = verPts + namePts + addrPts + phonePts + descPts + websitePts + hoursPts + labelPts + catBasePts + catExtraPts + catCityPts + socialPts + appointmentsPts + serviceAreaPts + bookApptPts + qaPts + photosPts + reviewRatingPts + responseRatePts + reviewsPts + velocityScore + gmbFeedScore;
        const roundedTotal = Math.round(totalScoreAll);
        setProfileScore(roundedTotal);
        try {
          const locNameForSave = (targetLocation && targetLocation.name) || (business && business.name) || '';
          const derivedLocIdForSave = locNameForSave ? String(locNameForSave).split('/').pop() : (business?.locationId || business?.id || '');
          const addressToSave = (displayAddress || '').trim();
          const keywordsToSave = Array.isArray(keywordsState) ? keywordsState : [];
          if (derivedLocIdForSave) {
            await saveProfileStrengthSnapshot({
              locationId: derivedLocIdForSave,
              score: roundedTotal,
              address: addressToSave,
              keywords: keywordsToSave,
              breakdown: {
                verification: verPts,
                business_name_contains_city: namePts,
                address: addrPts,
                phone_number: phonePts,
                description_length: descPts,
                website_link: websitePts,
                timings: hoursPts,
                labels: labelPts,
                categories_primary: catBasePts,
                categories_additional: catExtraPts,
                category_mentions_city: catCityPts,
                social_media_attached: socialPts,
                appointments_link: appointmentsPts,
                service_area: serviceAreaPts,
                book_appointment: bookApptPts,
                q_and_a_section_present: qaPts,
                photos: photosPts,
                review_rating: reviewRatingPts,
                response_rate: responseRatePts,
                reviews_vs_competitors: reviewsPts,
                velocity_score: velocityScore,
                gmb_feed: gmbFeedScore
              }
            });
          }
        } catch (persistErr) {
          logger.warn('[ProfileStrengthResults] Persist snapshot failed:', persistErr);
        }
        logger.log('[ProfileStrengthResults] Analysis complete, setting isCalculating to false. Final score:', roundedTotal);
        setIsCalculating(false);
        // Map details to existing in-scope variables to avoid no-undef lints
        const hasAddress = !!displayAddress;
        const labelCount = Array.isArray(targetLocation?.labels) ? targetLocation.labels.length : 0;
        const additionalCount = typeof extraCount === 'number' ? extraCount : 0;
        const catMentionsCity = typeof hasCityInCategory === 'boolean' ? hasCityInCategory : false;
        const descLength = typeof wordCount === 'number' ? wordCount : 0;

        setScoreBreakdown([
          { factor: 'Verification', details: { verified: isVerified }, points: verPts },
          { factor: 'Business name contains city', details: { hasCity }, points: namePts },
          { factor: 'Address', details: { hasAddress, hasCity, hasKeyword, addr: displayAddress }, points: addrPts },
          {
            factor: 'Phone number', details: {
              hasPhone: phoneScoreResult.analysis.hasPhone,
              isUnique: phoneScoreResult.analysis.isUnique,
              linkedBusinesses: phoneScoreResult.analysis.linkedBusinesses,
              reason: phoneScoreResult.analysis.reason,
              phoneNumber: primaryPhone,
              otherBusinessNames: phoneScoreResult.analysis.otherBusinessNames
            }, points: phonePts
          },
          { factor: 'Description length', details: { descLength }, points: descPts },
          { factor: 'Website link', details: { hasWebsite }, points: websitePts },
          { factor: 'Timings (hours)', details: { hasHours }, points: hoursPts },
          { factor: 'Labels', details: { labelCount, hasLabels }, points: labelPts },
          { factor: 'Categories (primary)', details: { hasPrimary }, points: catBasePts },
          { factor: 'Categories (additional)', details: { additionalCount }, points: catExtraPts },
          { factor: 'Category mentions city', details: { catMentionsCity }, points: catCityPts },
          { factor: 'Social media attached', details: { hasSocial }, points: socialPts },
          { factor: 'Appointments link', details: { hasAppt }, points: appointmentsPts },
          { factor: 'Service area', details: { hasServiceArea }, points: serviceAreaPts },
          { factor: 'Book appointment (explicit)', details: { hasBookAppointment }, points: bookApptPts },
          { factor: 'Q&A section present', details: { hasQASection }, points: qaPts },
          {
            factor: 'Photos', details: {
              businessType: isHotelRestaurantBusiness ? 'Hotel/Restaurant' : 'Regular',
              totalPhotos,
              hasCoverPhoto,
              hasLogoPhoto,
              hasMenuPhotos: isHotelRestaurantBusiness ? hasMenuPhotos : undefined,
              coverPoints: coverPts,
              logoPoints: logoPts,
              menuPoints: isHotelRestaurantBusiness ? menuPts : undefined,
              volumePoints: volumePts
            }, points: photosPts
          },
          { factor: 'Review Rating', details: { myRating, capped: reviewRatingPts === 20 }, points: reviewRatingPts },
          { factor: 'Response Rate', details: { responseRate: `${responseRate}%` }, points: responseRatePts },
          { factor: 'Reviews vs competitors', details: { myTotal, compAvg }, points: reviewsPts },
          {
            factor: 'Velocity Score', details: {
              velocityScore: velocityScore,
              reviewsLast30Days: reviewsLast30Days,
              competitorsAnalyzed: competitors.length,
              benchmarkReviewCount: 20,
              velocityPercentage: (velocityScore / 12) * 100,
              velocityRecommendations: velocityRecommendations
            }, points: velocityScore
          },
          {
            factor: 'GMB Feed', details: {
              postsLast90Days: postsLast90Days,
              benchmarkPostCount: 8,
              feedPercentage: (gmbFeedScore / 30) * 100,
              feedLevel: gmbFeedLevel,
              feedRecommendations: gmbFeedRecommendations
            }, points: gmbFeedScore
          },
          { factor: 'TOTAL (raw)', details: {}, points: score + velocityScore + gmbFeedScore }
        ]);
      }
    };
    logger.log('[ProfileStrengthResults] About to call computeScore()');
    computeScore();
    return () => { isMounted = false; };
  }, [account?.verificationState, targetLocation, displayAddress, keywordsState, business?.title, business?.businessName, state?.startAnalysis]);

  // Force start analysis when startAnalysis flag is present (only once)
  useEffect(() => {
    logger.log('[ProfileStrengthResults] useEffect for startAnalysis flag triggered');
    logger.log('[ProfileStrengthResults] state?.startAnalysis:', state?.startAnalysis);
    logger.log('[ProfileStrengthResults] hasProcessedStartFlag:', hasProcessedStartFlag);
    logger.log('[ProfileStrengthResults] isCalculating:', isCalculating);

    if (state?.startAnalysis === true && !hasProcessedStartFlag && !isCalculating) {
      logger.log('[ProfileStrengthResults] Starting analysis due to startAnalysis flag (first time only)');
      setHasProcessedStartFlag(true);
      setIsCalculating(true);
    }
  }, [state?.startAnalysis, hasProcessedStartFlag, isCalculating]);

  // Debug component mount
  useEffect(() => {
    logger.log('[ProfileStrengthResults] Component mounted');
    logger.log('[ProfileStrengthResults] Full location state:', location.state);
    logger.log('[ProfileStrengthResults] Navigation state keys:', Object.keys(location.state || {}));
  }, []);

  // Edit icon now opens the keyword editor dialog
  const openKeywordEditor = () => setEditKwOpen(true);

  // Export current score breakdown to CSV (one row per factor)
  const handleExportCSV = () => {
    try {
      const rows = [];
      // Header/summary rows
      rows.push(['Report', 'Profile Strength Results']);
      rows.push(['Generated At', new Date().toISOString()]);
      rows.push(['Account', resolvedAccountName || '']);
      rows.push(['Location', displayAddress || '']);
      rows.push(['Total Score', `${profileScore} / 300`]);
      rows.push([]); // blank line

      // Column headers for breakdown
      rows.push(['Factor', 'Points', 'Details']);

      const toDetailString = (details) => {
        if (!details || typeof details !== 'object') return '';
        try {
          return Object.entries(details)
            .map(([k, v]) => `${k}=${Array.isArray(v) ? v.join('|') : String(v)}`)
            .join('; ');
        } catch {
          return JSON.stringify(details);
        }
      };

      (Array.isArray(scoreBreakdown) ? scoreBreakdown : []).forEach(item => {
        rows.push([
          item?.factor ?? '',
          typeof item?.points === 'number' ? item.points : '',
          toDetailString(item?.details)
        ]);
      });

      // Convert to CSV text
      const csv = rows
        .map(row => row.map(cell => {
          const text = cell == null ? '' : String(cell);
          // Escape quotes and wrap if needed
          const escaped = text.replace(/"/g, '""');
          return /[",\n]/.test(escaped) ? `"${escaped}"` : escaped;
        }).join(','))
        .join('\n');

      const blob = new Blob(["\uFEFF" + csv], { type: 'text/csv;charset=utf-8;' }); // BOM for Excel
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const niceName = (resolvedAccountName || 'profile').replace(/[^a-z0-9\-]+/gi, '_').toLowerCase();
      a.href = url;
      a.download = `${niceName}_profile_strength.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      logger.error('[Export CSV] Failed to export CSV:', err);
    }
  };

  const toggleKeyword = (kw) => {
    setKeywordsState((prev) => {
      const exists = prev.includes(kw);
      if (exists) return prev.filter(k => k !== kw);
      if (prev.length >= keywordLimit) return prev; // limit based on plan
      return [...prev, kw];
    });
  };

  const handleSaveKeywords = () => {
    // Merge manual keywords (comma-separated), enforce limit based on plan
    const manualParts = manualKw.split(',').map(s => s.trim()).filter(Boolean);
    setKeywordsState((prev) => [...prev, ...manualParts].slice(0, keywordLimit));
    setEditKwOpen(false);
  };

  // Define all analysis steps for the loading UI
  const analysisSteps = [
    'Verification Status',
    'Business Name Analysis',
    'Address Analysis',
    'Contact Information',
    'Description Analysis',
    'Website Analysis',
    'Business Hours',
    'Labels Analysis',
    'Categories & Services',
    'Competitor Analysis',
    'Final Score Calculation'
  ];

  return (
    <DashboardLayout>
      <MainContent>
        <ContentSection style={{ position: 'relative' }}>
          <AnalysisLoadingPopup open={isCalculating} onComplete={() => { }} />

          <HeaderSection>
            <MainTitle>Profile Strength</MainTitle>
            <Description>
              Unlock advanced insights to grow your visibility, attract more customers, and dominate local search
            </Description>
          </HeaderSection>

          <SummaryCard>
            <SummaryColumn>
              <SummaryLabel>Select Account*</SummaryLabel>
              <SummaryValue>{resolvedAccountName || 'â€”'}</SummaryValue>
            </SummaryColumn>

            <VerticalDivider />

            <SummaryColumn>
              <SummaryLabel>Selected Keyword*</SummaryLabel>
              <SummaryValue>{keywordsState.length ? keywordsState.join(', ') : 'â€”'}</SummaryValue>
            </SummaryColumn>

            <VerticalDivider />

            <SummaryColumn>
              <SummaryLabel>Location*</SummaryLabel>
              <SummaryValue>{displayAddress || 'â€”'}</SummaryValue>
            </SummaryColumn>

            <VerticalDivider />

            <SummaryColumn>
              <SummaryLabel>Analysis Speed</SummaryLabel>
              <FormControlLabel
                control={<Switch checked={fastAnalysis} onChange={(e) => setFastAnalysis(e.target.checked)} />}
                label={fastAnalysis ? 'Instant' : 'Normal'}
              />
            </SummaryColumn>

            <EditButton onClick={openKeywordEditor}>
              <EditIcon width={17} height={17} color="#0B91D6" />
            </EditButton>
          </SummaryCard>

          <ResultsSection>
            <ScoreSection>
              <ScoreLabel>Profile Strength Score</ScoreLabel>
              <ScoreValue>{profileScore} / 300</ScoreValue>
              <ScoreSubtitle>Current Score</ScoreSubtitle>

              {/* Profile strength gauge visualization */}
              <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', mt: 2 }}>
                {isCalculating ? (
                  <CircularProgress size={32} style={{ color: '#ffffff' }} />
                ) : (
                  <Box sx={{ transform: 'scale(0.85)' }}>
                    <SemiCircularGauge score={profileScore} maxScore={300} title="" />
                  </Box>
                )}
              </Box>
            </ScoreSection>

            <AboutSection>
              <AboutTitle>About Company Strength</AboutTitle>
              <AboutText>
                • Detailed Competitor Analysis
                <br />• AI-Powered Recommendations
                <br />• Review & Post Performance Tracking
                <br />• Local Keyword & Ranking Insights
                <br />• Automated Task System to Improve Your Score
              </AboutText>

              <LegendContainer>
                {legendItems.map((item, index) => (
                  <LegendItem key={index}>
                    <LegendDot color={item.color} />
                    <LegendText>{item.label}</LegendText>
                  </LegendItem>
                ))}
              </LegendContainer>
            </AboutSection>
          </ResultsSection>

          {isAdmin && (
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <ExportCSVButton onClick={handleExportCSV}>
                Export CSV
              </ExportCSVButton>
            </div>
          )}

        </ContentSection>
      </MainContent>

      <Dialog open={editKwOpen} onClose={() => setEditKwOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Edit Keywords (max {keywordLimit} - {currentPlan} plan)</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {predefinedKeywords.map((kw) => (
              <Chip
                key={kw}
                label={kw}
                color={keywordsState.includes(kw) ? 'primary' : 'default'}
                onClick={() => toggleKeyword(kw)}
                sx={{ cursor: 'pointer' }}
              />
            ))}
          </Box>
          <TextField
            fullWidth
            label="Add manual keywords (comma-separated)"
            placeholder="e.g., Spa, Zirakpur"
            value={manualKw}
            onChange={(e) => setManualKw(e.target.value)}
          />
          <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>Currently selected: {keywordsState.join(', ') || 'â€”'}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditKwOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveKeywords}>Save</Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
};

export default ProfileStrengthResults;








